import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TaskService from "./TaskService";
import AuditHistoryDialog from "./AuditHistoryDialog";
import { employeeHasAccessToApplication } from "../utils/employeeApplicationAccess";
import "./AllTasksScreen.css";

/** Normalize persisted task owner filter (array, comma string, or legacy single string). */
function normalizeSavedTaskOwnerIds(raw) {
  if (Array.isArray(raw)) {
    return raw.map((s) => String(s).trim()).filter(Boolean);
  }
  if (typeof raw === "string" && raw.trim()) {
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

/** Same age buckets + green→red palette as Ticket Ageing Report. */
const AGING_BUCKET_FILTER_OPTIONS = [
  { value: "LT_7", label: "Under 7 days", color: "#bbf7d0" }, // light green
  { value: "LT_30", label: "Under 30 days", color: "#4ade80" }, // medium green
  { value: "LT_45", label: "Under 45 days", color: "#fef08a" }, // light yellow
  { value: "LT_60", label: "Under 60 days", color: "#facc15" }, // medium yellow
  { value: "LT_90", label: "Under 90 days", color: "#fdba74" }, // light orange
  { value: "LT_180", label: "Under 180 days", color: "#ea580c" }, // dark orange
  { value: "GT_180", label: "Over 180 days", color: "#b91c1c" }, // dark red
];

function getAgingBucketCodeForDays(days) {
  const n = Number(days);
  if (Number.isNaN(n)) return null;
  if (n <= 7) return "LT_7";
  if (n <= 30) return "LT_30";
  if (n <= 45) return "LT_45";
  if (n <= 60) return "LT_60";
  if (n <= 90) return "LT_90";
  if (n <= 180) return "LT_180";
  return "GT_180";
}

function taskMatchesAgingBuckets(ticketAgeDays, selectedBuckets) {
  if (!Array.isArray(selectedBuckets) || selectedBuckets.length === 0) return true;
  const code = getAgingBucketCodeForDays(ticketAgeDays);
  return code != null && selectedBuckets.includes(code);
}

function emptyAgingBucketTotals() {
  return Object.fromEntries(AGING_BUCKET_FILTER_OPTIONS.map((b) => [b.value, 0]));
}

function aggregateAgingBucketTotalsFromSummary(summaryRows) {
  const totals = emptyAgingBucketTotals();
  let totalTickets = 0;
  for (const row of summaryRows || []) {
    for (const bucket of AGING_BUCKET_FILTER_OPTIONS) {
      totals[bucket.value] += Number(row[bucket.value] || 0);
    }
    totalTickets += Number(row.total_tickets || 0);
  }
  return { totals, totalTickets };
}

function aggregateAgingBucketTotalsFromTasks(tasks) {
  const totals = emptyAgingBucketTotals();
  let totalTickets = 0;
  for (const task of tasks || []) {
    const code = task.agingBucketCode || getAgingBucketCodeForDays(task.ticketAgeDays);
    if (!code || totals[code] === undefined) continue;
    totals[code] += 1;
    totalTickets += 1;
  }
  return { totals, totalTickets };
}

function buildAgingDonutGradient(totals, totalTickets) {
  if (!totalTickets) return "conic-gradient(#e2e8f0 0deg, #e2e8f0 360deg)";
  let cursor = 0;
  const stops = [];
  for (const bucket of AGING_BUCKET_FILTER_OPTIONS) {
    const count = Number(totals[bucket.value] || 0);
    if (count <= 0) continue;
    const pct = (count / totalTickets) * 100;
    const start = cursor * 3.6;
    cursor += pct;
    const end = cursor * 3.6;
    stops.push(`${bucket.color} ${start}deg ${end}deg`);
  }
  return stops.length
    ? `conic-gradient(${stops.join(", ")})`
    : "conic-gradient(#e2e8f0 0deg, #e2e8f0 360deg)";
}

// TypeAhead Dropdown Component (unchanged)
const TypeAheadDropdown = ({
  label,
  options = [],
  selectedValues = [],
  onToggle,
  placeholder = "Type to search...",
  displayKey = null,
  valueKey = null,
  customStyle = {},
  showAllOption = false,
  maxHeight = 200,
  minCharsToSearch = 0,
  noResultsText = "No results found",
  isMultiSelect = true,
  disabled = false,
  clearable = true,
  highlightMatches = true,
  sleek = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [localSelectedValues, setLocalSelectedValues] = useState(selectedValues);
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const dropdownContentRef = useRef(null);
  const optionRefs = useRef([]);

  // Sync local state with parent state
  useEffect(() => {
    setLocalSelectedValues(selectedValues);
  }, [selectedValues]);

  // Filter options based on search term
  useEffect(() => {
    if (searchTerm.length < minCharsToSearch) {
      setFilteredOptions(options);
      return;
    }

    const q = searchTerm.toLowerCase();
    const filtered = options.filter((option) => {
      if (displayKey) {
        const primary = String(option[displayKey] ?? "").toLowerCase();
        if (primary.includes(q)) return true;
        if (option.email != null && String(option.email).toLowerCase().includes(q)) return true;
        if (option.department != null && String(option.department).toLowerCase().includes(q)) {
          return true;
        }
        return false;
      }
      const searchText = option;
      return String(searchText).toLowerCase().includes(q);
    });

    setFilteredOptions(filtered);
    setHighlightedIndex(-1);
  }, [searchTerm, options, displayKey, minCharsToSearch]);

  // Highlight matching text
  const highlightText = (text, searchTerm) => {
    if (!highlightMatches || !searchTerm) return text;
    
    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === searchTerm.toLowerCase() ? 
        <mark key={index} style={{ backgroundColor: '#fff3cd', padding: 0 }}>{part}</mark> : 
        part
    );
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          const option = filteredOptions[highlightedIndex];
          const value = valueKey ? option[valueKey] : option;
          handleItemToggle(value);
        }
        break;
      
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        inputRef.current?.blur();
        break;
      
      case 'Tab':
        setIsOpen(false);
        break;
      
      default:
        break;
    }
  };

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [highlightedIndex]);

  // Handle clicks outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true);
    if (filteredOptions.length > 0) {
      setHighlightedIndex(0);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (!isOpen) {
      setIsOpen(true);
    }
    
    setHighlightedIndex(0);
  };

  // Handle item selection
  const handleItemToggle = (value) => {
    if (!isMultiSelect) {
      // Single select mode
      setLocalSelectedValues([value]);
      onToggle(value, 'single');
      setIsOpen(false);
      setSearchTerm('');
      inputRef.current?.blur();
    } else {
      // Multi select mode (normalize with String so chip lookup matches option id types)
      setLocalSelectedValues((prev) => {
        const vs = String(value);
        if (prev.some((v) => String(v) === vs)) {
          return prev.filter((v) => String(v) !== vs);
        }
        return [...prev, value];
      });
      onToggle(value);
    }
  };

  // Handle select all
  const handleSelectAll = () => {
    if (localSelectedValues.length === options.length) {
      // Deselect all
      localSelectedValues.forEach(value => onToggle(value));
    } else {
      // Select all
      options.forEach((option) => {
        const value = valueKey ? option[valueKey] : option;
        if (!localSelectedValues.some((v) => String(v) === String(value))) {
          onToggle(value);
        }
      });
    }
  };

  // Clear all selections
  const handleClear = () => {
    if (!isMultiSelect) {
      setLocalSelectedValues([]);
      if (onToggle) onToggle(null, "single");
      setSearchTerm("");
      return;
    }
    localSelectedValues.forEach((value) => onToggle(value));
    setSearchTerm("");
  };

  // Get display text for input
  const getDisplayText = () => {
    if (!isMultiSelect && localSelectedValues.length === 1) {
      const selected = localSelectedValues[0];
      if (displayKey && valueKey) {
        const option = options.find((opt) =>
          String(valueKey ? opt[valueKey] : opt) === String(selected)
        );
        return option ? (displayKey ? option[displayKey] : option) : selected;
      }
      return selected;
    }
    return '';
  };

  // Get placeholder text
  const getPlaceholderText = () => {
    if (isMultiSelect && localSelectedValues.length > 0) {
      return `${localSelectedValues.length} selected`;
    }
    return placeholder;
  };

  // Check if option is selected
  const isOptionSelected = (option) => {
    const value = valueKey ? option[valueKey] : option;
    return localSelectedValues.some((v) => String(v) === String(value));
  };

  const isAllSelected = localSelectedValues.length === options.length && options.length > 0;

  /** Plain-text tooltip for list rows, chips, and input (native `title` on hover). */
  const getOptionTitle = (option, displayValue) => {
    const main =
      displayValue !== undefined && displayValue !== null
        ? String(displayValue).trim()
        : "";
    if (option && typeof option === "object") {
      const parts = [];
      if (main) parts.push(main);
      const email = option.email != null ? String(option.email).trim() : "";
      if (email) parts.push(email);
      const dept = option.department != null ? String(option.department).trim() : "";
      if (dept) parts.push(dept);
      return parts.length ? parts.join(" · ") : main;
    }
    return main || "";
  };

  const selectedValuesTooltip = (() => {
    if (!isMultiSelect || localSelectedValues.length === 0) return "";
    return localSelectedValues
      .map((selectedValue) => {
        const opt = options.find((o) =>
          String(valueKey ? o[valueKey] : o) === String(selectedValue)
        );
        const displayText = opt
          ? displayKey
            ? opt[displayKey]
            : opt
          : selectedValue;
        return getOptionTitle(opt, displayText);
      })
      .filter(Boolean)
      .join("; ");
  })();

  const singleSelectInputTooltip = (() => {
    if (isMultiSelect) return "";
    if (localSelectedValues.length !== 1) return "";
    const text = getDisplayText();
    return text !== undefined && text !== null ? String(text) : "";
  })();

  const inputTitle =
    searchTerm.trim() !== ""
      ? searchTerm.trim()
      : isMultiSelect
        ? selectedValuesTooltip || getPlaceholderText()
        : singleSelectInputTooltip || getPlaceholderText();

  const inputStyle = sleek
    ? {
        width: '100%',
        boxSizing: 'border-box',
        cursor: disabled ? 'not-allowed' : 'text',
        opacity: disabled ? 0.65 : 1
      }
    : {
        width: '100%',
        padding: '8px 32px 8px 12px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        fontSize: '11px',
        fontFamily: 'inherit',
        backgroundColor: disabled ? '#f5f5f5' : 'white',
        cursor: disabled ? 'not-allowed' : 'text',
        height: '36px',
        boxSizing: 'border-box'
      };

  const fieldBlock = (
    <div className={sleek ? 'typeahead-dropdown__field-inner' : undefined} style={sleek ? undefined : { position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        value={searchTerm || getDisplayText()}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={getPlaceholderText()}
        disabled={disabled}
        className={sleek ? 'typeahead-dropdown__input-sleek' : undefined}
        style={inputStyle}
        autoComplete="off"
        title={inputTitle || undefined}
      />

      <div
        className={sleek ? 'typeahead-dropdown__chevron' : undefined}
        style={
          sleek
            ? undefined
            : {
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none',
                fontSize: '9px',
                color: '#666'
              }
        }
        aria-hidden="true"
      >
        {isOpen ? '▲' : '▼'}
      </div>

      {clearable && (searchTerm || localSelectedValues.length > 0) && !disabled && (
        <button
          type="button"
          className={sleek ? 'typeahead-dropdown__clear-btn' : undefined}
          onClick={(e) => {
            e.stopPropagation();
            if (searchTerm) {
              setSearchTerm('');
              inputRef.current?.focus();
            } else {
              handleClear();
            }
          }}
          style={
            sleek
              ? undefined
              : {
                  position: 'absolute',
                  right: '24px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '11px',
                  color: '#999',
                  padding: '0',
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }
          }
          title="Clear"
        >
          ✕
        </button>
      )}
    </div>
  );

  const rootClassName = [
    "typeahead-dropdown",
    sleek ? "typeahead-dropdown--sleek" : "",
    isOpen ? "typeahead-dropdown--open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      ref={dropdownRef}
      className={rootClassName}
      style={{
        position: 'relative',
        width: '100%',
        ...customStyle
      }}
      data-dropdown-container="true"
    >
      {sleek ? (
        <div className="all-tasks-filter-control-shell typeahead-dropdown__shell">
          {fieldBlock}
        </div>
      ) : (
        <div style={{ position: 'relative' }}>{fieldBlock}</div>
      )}

      {/* Selected Items Display (for multi-select) */}
      {isMultiSelect && localSelectedValues.length > 0 && (
        <div
          className="typeahead-dropdown__tags"
          style={{
            marginTop: '2px',
            marginBottom: '2px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '3px'
          }}
        >
          {localSelectedValues.slice(0, 3).map((selectedValue, index) => {
            const option = options.find((opt) =>
              String(valueKey ? opt[valueKey] : opt) === String(selectedValue)
            );
            const displayText = option 
              ? (displayKey ? option[displayKey] : option) 
              : selectedValue;
            
            const chipTitle = getOptionTitle(option, displayText);

            return (
              <span
                key={index}
                title={chipTitle || undefined}
                style={{
                  backgroundColor: '#e3f2fd',
                  color: '#1976d2',
                  padding: '2px 6px',
                  borderRadius: '12px',
                  fontSize: '9px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  maxWidth: '120px'
                }}
              >
                <span style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {displayText}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleItemToggle(selectedValue);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '8px',
                    padding: '0',
                    color: '#1976d2'
                  }}
                >
                  ✕
                </button>
              </span>
            );
          })}
          {localSelectedValues.length > 3 && (
            <span
              title={localSelectedValues
                .slice(3)
                .map((selectedValue) => {
                  const opt = options.find((o) =>
                    String(valueKey ? o[valueKey] : o) === String(selectedValue)
                  );
                  const displayText = opt
                    ? displayKey
                      ? opt[displayKey]
                      : opt
                    : selectedValue;
                  return getOptionTitle(opt, displayText);
                })
                .filter(Boolean)
                .join("; ")}
              style={{
              backgroundColor: '#f5f5f5',
              color: '#666',
              padding: '2px 6px',
              borderRadius: '12px',
              fontSize: '9px'
            }}
            >
              +{localSelectedValues.length - 3} more
            </span>
          )}
        </div>
      )}

      {/* Dropdown Content */}
      {isOpen && !disabled && (
        <>
          <div 
            className="dropdown-overlay active"
            onClick={() => setIsOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9998,
              background: 'transparent'
            }}
          />
          
          <div
            ref={dropdownContentRef}
            className={sleek ? 'typeahead-dropdown__menu typeahead-dropdown__menu--sleek' : undefined}
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: '4px',
              maxHeight: `${maxHeight}px`,
              overflowY: 'auto',
              zIndex: 9999,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              marginTop: '2px'
            }}
          >
            {/* Select All Option */}
            {isMultiSelect && showAllOption && filteredOptions.length > 0 && (
              <div
                title={`Select all ${options.length} items`}
                style={{
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  backgroundColor: isAllSelected ? '#f0f0f0' : 'white',
                  borderBottom: '1px solid #eee',
                  fontWeight: 'bold',
                  fontSize: '11px'
                }}
                onClick={handleSelectAll}
              >
                <span style={{
                  display: 'inline-block',
                  width: '12px',
                  height: '12px',
                  border: '1px solid #ccc',
                  marginRight: '8px',
                  backgroundColor: isAllSelected ? '#007bff' : 'white',
                  borderRadius: '2px',
                  position: 'relative'
                }}>
                  {isAllSelected && (
                    <span style={{
                      position: 'absolute',
                      left: '2px',
                      top: '-1px',
                      color: 'white',
                      fontSize: '9px'
                    }}>✓</span>
                  )}
                </span>
                Select All ({options.length})
              </div>
            )}

            {/* Options List */}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const value = valueKey ? option[valueKey] : option;
                const display = displayKey ? option[displayKey] : option;
                const isSelected = isOptionSelected(option);
                const isHighlighted = index === highlightedIndex;
                const optionTitle = getOptionTitle(option, display);

                return (
                  <div
                    key={index}
                    ref={el => optionRefs.current[index] = el}
                    title={optionTitle || undefined}
                    style={{
                      padding: '8px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      backgroundColor: isHighlighted 
                        ? '#e3f2fd' 
                        : isSelected 
                        ? '#f0f0f0' 
                        : 'white',
                      fontSize: '11px',
                      borderLeft: isHighlighted ? '3px solid #1976d2' : '3px solid transparent'
                    }}
                    onClick={() => handleItemToggle(value)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {isMultiSelect && (
                      <span style={{
                        display: 'inline-block',
                        width: '12px',
                        height: '12px',
                        border: '1px solid #ccc',
                        marginRight: '8px',
                        backgroundColor: isSelected ? '#007bff' : 'white',
                        borderRadius: '2px',
                        position: 'relative'
                      }}>
                        {isSelected && (
                          <span style={{
                            position: 'absolute',
                            left: '2px',
                            top: '-1px',
                            color: 'white',
                            fontSize: '9px'
                          }}>✓</span>
                        )}
                      </span>
                    )}
                    <span
                      title={optionTitle || undefined}
                      style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1
                    }}
                    >
                      {highlightText(display, searchTerm)}
                    </span>
                  </div>
                );
              })
            ) : (
              <div style={{
                padding: '12px',
                textAlign: 'center',
                color: '#999',
                fontSize: '11px',
                fontStyle: 'italic'
              }}>
                {searchTerm ? noResultsText : 'Start typing to search...'}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const AllTasksScreen = ({ userData = {} }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUserId = localStorage.getItem("employee_uid");
  const userRoleId = localStorage.getItem("user_role_id");

  // Single ref to track if initial data loading is complete
  const initialLoadComplete = useRef(false);
  const isComponentMounted = useRef(true);
  const defaultStatusesUpdated = useRef(false);
  const initialTaskFetchDone = useRef(false);
  const filtersRestoredFromNavigation = useRef(false);
  const filtersLoadedFromStorage = useRef(false);
  const fetchTasksRef = useRef(null);

  console.log("LocalStorage Contents:");
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    console.log(`${key}: ${localStorage.getItem(key)}`);
  }

  console.log("Current User ID from localStorage:", currentUserId);
  console.log("Current User ID type:", typeof currentUserId);
  console.log("Current User Role ID:", userRoleId);
  console.log("Navigation location state:", location.state);

  // Reset filters only when opening All Tasks from the classic dashboard (not internal tiles)
  const isComingFromDashboard = location.state?.from === "dashboard";

  // Memoized helper functions
  const getDefaultOwnershipFilter = useCallback(() => {
    // Always default to "Assigned to Me" regardless of role
    return "Assigned";
  }, []);

  const getFilteredApplications = useCallback((allApplications) => {
    if (!allApplications || allApplications.length === 0) {
      console.warn("⚠️ No applications provided to getFilteredApplications");
      return [];
    }
    
    const defaultApplicationId = localStorage.getItem("default_application_id");
    console.log("🔍 getFilteredApplications - userRoleId:", userRoleId, "defaultApplicationId:", defaultApplicationId);
    console.log("🔍 getFilteredApplications - allApplications count:", allApplications.length);
    
    if (userRoleId === "3" && defaultApplicationId) {
      // Handle comma-separated application IDs (e.g., "3,1")
      const appIds = defaultApplicationId.split(',').map(id => id.trim()).filter(id => id);
      const appIdNumbers = appIds.map(id => parseInt(id, 10)).filter(id => !isNaN(id));
      
      console.log("🔍 getFilteredApplications - Parsed app IDs:", appIdNumbers);
      
      if (appIdNumbers.length > 0) {
        const filtered = allApplications.filter(app => 
          appIdNumbers.includes(app.aa_app_rl_application_id)
        );
        console.log("🔍 getFilteredApplications - Filtered applications count:", filtered.length);
        
        // If filtering results in empty array, return all applications as fallback
        if (filtered.length === 0) {
          console.warn("⚠️ Filtering resulted in empty array, returning all applications as fallback");
          return allApplications;
        }
        
        return filtered;
      }
    }
    
    console.log("🔍 getFilteredApplications - Returning all applications:", allApplications.length);
    return allApplications;
  }, [userRoleId]);

  // Memoized default filter state calculation
  const getDefaultFilterState = useCallback(() => {
    return {
      selectedStatuses: ["In Progress", "Not started"],
      selectedPriorities: [],
      selectedApplications: [], // No default application filter - always empty
      selectedReleaseTypes: [],
      selectedTaskTypes: [],
      selectedAgingBuckets: [],
      internalOverdueClientFilter: false,
      internalUnassignedClientFilter: false,
      fromInternalPreset: false,
      searchTerm: "",
      ownershipFilter: getDefaultOwnershipFilter(), // Always "Assigned"
      searchMode: "basic",
      searchPanelExpanded: true,
      selectedTaskOwnerIds: [],
      selectedCreatedByIds: [],
      releaseDateFilter: "",
    };
  }, [getDefaultOwnershipFilter]);

  // Get saved filter state or defaults
  const getSavedFilterState = useCallback(() => {
    if (location.state?.from === "internal-dashboard" && location.state?.preset) {
      const p = location.state.preset;
      filtersLoadedFromStorage.current = false;
      const base = getDefaultFilterState();
      const appIds = Array.isArray(p.applicationIds)
        ? p.applicationIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n))
        : [];
      const presetStatuses = Array.isArray(p.selectedStatuses) ? p.selectedStatuses : [];
      const statusesWithoutUnassigned = presetStatuses.filter(
        (s) => String(s || "").trim().toLowerCase() !== "unassigned"
      );
      const useOverdueDateFilter = !!p.internalOverdueClientFilter;
      return {
        ...base,
        selectedApplications: appIds,
        selectedPriorities: Array.isArray(p.selectedPriorities) ? p.selectedPriorities : [],
        selectedTaskTypes: Array.isArray(p.selectedTaskTypes) ? p.selectedTaskTypes : [],
        selectedAgingBuckets: Array.isArray(p.selectedAgingBuckets) ? p.selectedAgingBuckets : [],
        selectedStatuses:
          useOverdueDateFilter
            ? statusesWithoutUnassigned
            : statusesWithoutUnassigned.length > 0
              ? statusesWithoutUnassigned
              : base.selectedStatuses,
        searchMode: p.searchMode === "advanced" ? "advanced" : "basic",
        ownershipFilter: p.ownershipFilter || "All",
        searchPanelExpanded: p.searchPanelExpanded !== false,
        internalOverdueClientFilter: !!p.internalOverdueClientFilter,
        internalUnassignedClientFilter: !!p.internalUnassignedClientFilter,
        searchTerm: typeof p.searchTerm === "string" ? p.searchTerm : "",
        releaseDateFilter: typeof p.releaseDateFilter === "string" ? p.releaseDateFilter : "",
        fromInternalPreset: true,
      };
    }

    if (isComingFromDashboard) {
      filtersLoadedFromStorage.current = false;
      const defaults = getDefaultFilterState();
      const preset =
        typeof location.state?.presetSearchTerm === "string"
          ? location.state.presetSearchTerm.trim()
          : "";
      if (preset) {
        return { ...defaults, searchTerm: preset };
      }
      return defaults;
    }

    try {
      const savedFilters = localStorage.getItem("taskFilters");
      if (savedFilters) {
        const parsed = JSON.parse(savedFilters);
        
        // Ensure all application IDs are numbers (convert from strings if needed)
        // But don't force default applications - respect what user saved or use empty array
        let defaultApps = parsed.selectedApplications || [];
        defaultApps = defaultApps.map(appId => {
          const numId = typeof appId === 'string' ? parseInt(appId, 10) : appId;
          return isNaN(numId) ? appId : numId;
        });
        
        // Mark that we loaded filters from storage
        filtersLoadedFromStorage.current = true;
        console.log("📦 Loaded filters from localStorage:", parsed.selectedStatuses);
        console.log("📦 Loaded selectedApplications (converted to numbers):", defaultApps);
        
        // Always use "Assigned" as default, even if localStorage has "TaskOwner"
        const savedOwnershipFilter = parsed.ownershipFilter || getDefaultOwnershipFilter();
        const finalOwnershipFilter = savedOwnershipFilter === "TaskOwner" ? "Assigned" : savedOwnershipFilter;
        
        return {
          selectedStatuses: parsed.selectedStatuses || ["In Progress", "Not started"],
          selectedPriorities: parsed.selectedPriorities || [],
          selectedApplications: defaultApps, // Use saved value or empty array - no forced defaults
          selectedReleaseTypes: parsed.selectedReleaseTypes || [],
          selectedTaskTypes: Array.isArray(parsed.selectedTaskTypes) ? parsed.selectedTaskTypes : [],
          selectedAgingBuckets: Array.isArray(parsed.selectedAgingBuckets)
            ? parsed.selectedAgingBuckets
            : [],
          internalOverdueClientFilter: !!parsed.internalOverdueClientFilter,
          internalUnassignedClientFilter: !!parsed.internalUnassignedClientFilter,
          fromInternalPreset: false,
          searchTerm: parsed.searchTerm || "",
          ownershipFilter: finalOwnershipFilter,
          searchMode: parsed.searchMode === "advanced" ? "advanced" : "basic",
          searchPanelExpanded: parsed.searchPanelExpanded !== false,
          selectedTaskOwnerIds: normalizeSavedTaskOwnerIds(
            parsed.selectedTaskOwnerIds ?? parsed.taskOwnerFilter
          ),
          selectedCreatedByIds: Array.isArray(parsed.selectedCreatedByIds)
            ? parsed.selectedCreatedByIds.map((id) => String(id).trim()).filter(Boolean)
            : [],
          releaseDateFilter: typeof parsed.releaseDateFilter === "string" ? parsed.releaseDateFilter : "",
        };
      }
    } catch (error) {
      console.error("Error parsing saved filters:", error);
    }

    filtersLoadedFromStorage.current = false;
    return getDefaultFilterState();
  }, [
    isComingFromDashboard,
    userRoleId,
    getDefaultOwnershipFilter,
    getDefaultFilterState,
    location.state,
  ]);

  // Initialize state with saved values or defaults
  const initialFilterState = useMemo(() => getSavedFilterState(), [getSavedFilterState]);
  const fromInternalPresetRef = useRef(!!initialFilterState.fromInternalPreset);

  const [selectedStatuses, setSelectedStatuses] = useState(initialFilterState.selectedStatuses);
  const [selectedPriorities, setSelectedPriorities] = useState(initialFilterState.selectedPriorities);
  const [selectedApplications, setSelectedApplications] = useState(initialFilterState.selectedApplications);
  const [selectedReleaseTypes, setSelectedReleaseTypes] = useState(
    initialFilterState.selectedReleaseTypes || []
  );
  const [searchTerm, setSearchTerm] = useState(initialFilterState.searchTerm);
  const [ownershipFilter, setOwnershipFilter] = useState(initialFilterState.ownershipFilter);
  const [searchMode, setSearchMode] = useState(initialFilterState.searchMode || "basic");
  const [searchPanelExpanded, setSearchPanelExpanded] = useState(
    initialFilterState.searchPanelExpanded !== false
  );
  const [selectedTaskOwnerIds, setSelectedTaskOwnerIds] = useState(
    initialFilterState.selectedTaskOwnerIds || []
  );
  const [selectedCreatedByIds, setSelectedCreatedByIds] = useState(
    initialFilterState.selectedCreatedByIds || []
  );
  const [releaseDateFilter, setReleaseDateFilter] = useState(initialFilterState.releaseDateFilter || "");
  const [selectedTaskTypes, setSelectedTaskTypes] = useState(
    initialFilterState.selectedTaskTypes || []
  );
  const [selectedAgingBuckets, setSelectedAgingBuckets] = useState(
    initialFilterState.selectedAgingBuckets || []
  );
  const [internalOverdueClientFilter, setInternalOverdueClientFilter] = useState(
    !!initialFilterState.internalOverdueClientFilter
  );
  const [internalUnassignedClientFilter, setInternalUnassignedClientFilter] = useState(
    !!initialFilterState.internalUnassignedClientFilter
  );

  // State for tasks data and loading
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noDataFound, setNoDataFound] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  
  // State for dropdown data from backend
  const [statuses, setStatuses] = useState([]);
  const [applications, setApplications] = useState([]);
  const [releaseTypes, setReleaseTypes] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  /** Full employee list from lookup — never application-filtered (used for Created by). */
  const [masterEmployeeList, setMasterEmployeeList] = useState([]);
  /** Same source as master; Task Owner dropdown uses a subset via taskOwnerDropdownOptions. */
  const [ownerFilterEmployees, setOwnerFilterEmployees] = useState([]);
  
  // State for the distinct lead names
  const [leadNames, setLeadNames] = useState([]);

  // State for the audit history dialog
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [showAuditDialog, setShowAuditDialog] = useState(false);

  // State for column sorting - array of { column, direction } where direction is 'asc' or 'desc'
  const [columnSorts, setColumnSorts] = useState([]);

  // State to track if static data is loaded (to trigger initial task fetch)
  const [staticDataLoaded, setStaticDataLoaded] = useState(false);

  // Ticket ageing pie chart popover (search box dropdown)
  const [agingChartOpen, setAgingChartOpen] = useState(false);
  const [agingChartLoading, setAgingChartLoading] = useState(false);
  const [agingChartError, setAgingChartError] = useState(null);
  const [agingChartTotals, setAgingChartTotals] = useState(() => emptyAgingBucketTotals());
  const [agingChartTotalTickets, setAgingChartTotalTickets] = useState(0);
  const agingChartWrapRef = useRef(null);
  const pendingAgingChartSearchRef = useRef(false);

  // Function to check if user can create/edit tasks
  const canCreateOrEditTask = useCallback(() => {
    const isWrite = localStorage.getItem("is_write");
    
    if (userRoleId === "3") {
      return isWrite === "1";
    }
    
    return isWrite !== "0";
  }, [userRoleId]);

  // Function to get filtered task relation options based on user role
  const getTaskRelationOptions = useCallback(() => {
    const baseOptions = [
      { id: 'All', name: 'All Tasks' },
      { id: 'Assigned', name: 'Assigned to Me' },
      { id: 'TaskOwner', name: 'Tasks where I am the Owner' }
    ];

    if (userRoleId === "3") {
      return baseOptions;
    }

    return [
      ...baseOptions,
      ...leadNames.map(lead => ({ id: lead, name: lead }))
    ];
  }, [userRoleId, leadNames]);

  /**
   * Task owner list: only employees eligible for at least one selected application
   * (same rules as Create Task). No application selected → empty list until user picks Application(s).
   */
  const taskOwnerDropdownOptions = useMemo(() => {
    if (!ownerFilterEmployees.length) return [];
    const appIds = (selectedApplications || [])
      .map((id) => String(id).trim())
      .filter((id) => id !== "");
    if (appIds.length === 0) {
      return [];
    }
    return ownerFilterEmployees.filter((emp) =>
      appIds.some((appId) => employeeHasAccessToApplication(emp, appId))
    );
  }, [ownerFilterEmployees, selectedApplications]);

  /** Created by: always every employee from lookup (no application / role subset). */
  const createdByDropdownOptions = useMemo(() => {
    if (!masterEmployeeList.length) return [];
    return [...masterEmployeeList].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" })
    );
  }, [masterEmployeeList]);

  // Drop owner UIDs that are no longer in the application-scoped picker list
  useEffect(() => {
    if (ownerFilterEmployees.length === 0) return;
    const validSet = new Set(taskOwnerDropdownOptions.map((e) => String(e.id)));
    setSelectedTaskOwnerIds((prev) => {
      const next = prev.filter((id) => {
        const s = String(id);
        if (!/^\d+$/.test(s)) return true;
        return validSet.has(s);
      });
      return next.length === prev.length ? prev : next;
    });
  }, [taskOwnerDropdownOptions, ownerFilterEmployees.length]);

  // Drop created-by UIDs that are not in the full employee master list
  useEffect(() => {
    if (masterEmployeeList.length === 0) return;
    const validSet = new Set(masterEmployeeList.map((e) => String(e.id)));
    setSelectedCreatedByIds((prev) => {
      const next = prev.filter((id) => validSet.has(String(id)));
      return next.length === prev.length ? prev : next;
    });
  }, [masterEmployeeList]);

  // Save filter state whenever it changes (debounced)
  useEffect(() => {
    if (!initialLoadComplete.current) return;

    const timeoutId = setTimeout(() => {
      const filterState = {
        selectedStatuses,
        selectedPriorities,
        selectedApplications,
        selectedReleaseTypes,
        selectedTaskTypes,
        selectedAgingBuckets,
        internalOverdueClientFilter,
        internalUnassignedClientFilter,
        searchTerm,
        ownershipFilter,
        searchMode,
        searchPanelExpanded,
        selectedTaskOwnerIds,
        selectedCreatedByIds,
        releaseDateFilter,
      };
      localStorage.setItem("taskFilters", JSON.stringify(filterState));
    }, 300); // Debounce saving

    return () => clearTimeout(timeoutId);
  }, [
    selectedStatuses,
    selectedPriorities,
    selectedApplications,
    selectedReleaseTypes,
    selectedTaskTypes,
    selectedAgingBuckets,
    internalOverdueClientFilter,
    internalUnassignedClientFilter,
    searchTerm,
    ownershipFilter,
    searchMode,
    searchPanelExpanded,
    selectedTaskOwnerIds,
    selectedCreatedByIds,
    releaseDateFilter,
  ]);

  // Helper function to format date consistently
  const formatDateString = useCallback((dateStr) => {
    if (!dateStr) return "N/A";

    try {
      if (typeof dateStr === "string" && dateStr.includes(",")) {
        return dateStr;
      }

      const date = new Date(dateStr);
      
      if (isNaN(date.getTime())) {
        return "N/A";
      }

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (e) {
      console.error("Error formatting date:", dateStr, e);
      return "N/A";
    }
  }, []);

  /** YYYY-MM-DD in local time for date input and release-date filter matching */
  const toComparableCalendarDate = useCallback((dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);

  // Custom sorting function for status hierarchy
  const getStatusPriority = useCallback((status) => {
    const statusHierarchy = {
      'In Progress': 1,
      'In-Progress': 1,
      'In Testing': 2,
      'In-Testing': 2,
      'Completed': 3,
      'Complete': 3,
    };
    
    return statusHierarchy[status] || 999;
  }, []);

  // Helper function to transform tasks from API response
  const transformTasks = useCallback((tasksData) => {
    return (tasksData || []).map((task) => {
      console.log(
        `Raw task ID ${task.aa_app_rl_resource_task_uid} employee UID:`,
        task.aa_app_employee_uid
      );

      return {
        id: task.aa_app_rl_resource_task_uid,
        title: task.aa_app_rl_resource_task_title,
        employeeUid: task.aa_app_employee_uid,
        employeeName: task.aa_app_employee_name || "Unassigned",
        application: task.aa_app_rl_application_name || "Unknown",
        applicationId: task.aa_app_rl_application_id,
        status: task.aa_app_rl_task_status_name || "Unknown",
        statusId: task.aa_app_rl_task_status_id,
        priority: task.aa_app_rl_priority_name || "Unknown",
        priorityId: task.aa_app_rl_resource_task_priority_id,
        creationDate: formatDateString(task.aa_app_task_creation_date),
        updationDate: formatDateString(task.aa_app_task_updation_date),
        leadName: task.aa_app_rl_application_lead_name,
        leadId: task.aa_app_rl_application_lead_id,
        ownerName: task.aa_app_task_owner_name || "Unknown",
        ownerId: task.aa_app_task_owner_id,
        createdById: (() => {
          const raw =
            task.aa_app_task_created_by ??
            task.aa_app_task_created_by_id ??
            task.created_by_id ??
            task.task_created_by;
          if (raw === undefined || raw === null || raw === "") return null;
          return String(raw);
        })(),
        createdByName:
          task.aa_app_task_created_by_name ||
          task.aa_app_rl_created_by ||
          task.created_by_name ||
          null,
        taskType: task.aa_app_rl_task_type_shortname || task.aa_app_rl_task_type_longname || "Unknown",
        taskTypeId: task.aa_app_rl_task_type_id,
        taskTypeLongName: task.aa_app_rl_task_type_longname || task.aa_app_rl_task_type_shortname || "Unknown",
        ticketAgeDays: (() => {
          const raw = task.ticket_age_days;
          if (raw === undefined || raw === null || raw === "") return null;
          const n = Number(raw);
          return Number.isNaN(n) ? null : n;
        })(),
        agingBucketCode: getAgingBucketCodeForDays(task.ticket_age_days),
        releaseTypeId:
          task.aa_app_rl_release_type_id !== undefined &&
          task.aa_app_rl_release_type_id !== null &&
          task.aa_app_rl_release_type_id !== ""
            ? Number(task.aa_app_rl_release_type_id)
            : null,
        releaseTypeName: task.aa_app_rl_release_type_name || null,
        releaseDate: formatDateString(task.aa_app_rl_release_date),
        releaseDateCompare: toComparableCalendarDate(task.aa_app_rl_release_date),
      };
    });
  }, [formatDateString, toComparableCalendarDate]);

  const releaseTypeOptions = useMemo(() => {
    return (releaseTypes || [])
      .slice()
      .sort((a, b) => a.aa_app_rl_release_type_id - b.aa_app_rl_release_type_id)
      .map((rt) => ({
        value: rt.aa_app_rl_release_type_id,
        label: rt.aa_app_rl_release_type_name || `Type ${rt.aa_app_rl_release_type_id}`,
      }));
  }, [releaseTypes]);

  const taskTypeOptions = useMemo(() => {
    const map = new Map();

    (taskTypes || []).forEach((tt) => {
      const shortName = String(tt?.aa_app_rl_task_type_shortname || "").trim();
      const longName = String(tt?.aa_app_rl_task_type_longname || "").trim();
      const typeId = tt?.aa_app_rl_task_type_id;
      const value = shortName || longName || (typeId != null ? String(typeId) : "");
      if (!value) return;

      const label =
        shortName && longName && shortName.toLowerCase() !== longName.toLowerCase()
          ? `${shortName} (${longName})`
          : longName || shortName || `Type ${typeId}`;

      const key = value.toLowerCase();
      if (!map.has(key)) {
        map.set(key, { value, label });
      }
    });

    // Fallback: derive task types from loaded tasks when lookup is unavailable.
    (allTasks || []).forEach((task) => {
      const value = String(task?.taskType || "").trim();
      if (!value) return;
      const key = value.toLowerCase();
      if (!map.has(key)) {
        map.set(key, { value, label: value });
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    );
  }, [taskTypes, allTasks]);

  // Single effect to load initial static data (statuses, applications, priorities, …) from localStorage / master lookup
  useEffect(() => {
    let isCancelled = false;

    const loadInitialStaticData = async () => {
      if (initialLoadComplete.current) return;

      try {
        console.log("🚀 Starting initial static data load from localStorage...");
        
        // Load static data from localStorage
        const taskStatusDetailsStr = localStorage.getItem("task_status_details");
        const applicationDetailsStr = localStorage.getItem("application_details");
        const releaseTypeDetailsStr = localStorage.getItem("release_type_details");
        const taskTypeDetailsStr = localStorage.getItem("task_type_details");
        const taskPriorityDetailsStr = localStorage.getItem("task_priority_details");
        const employeeDetailsStr = localStorage.getItem("employee_details");

        if (isCancelled || !isComponentMounted.current) return;

        // Parse statuses from localStorage
        let statusesData = [];
        if (taskStatusDetailsStr) {
          try {
            statusesData = JSON.parse(taskStatusDetailsStr);
            console.log("✅ Task status details loaded from localStorage:", statusesData.length, "statuses");
          } catch (parseError) {
            console.error("❌ Error parsing task_status_details from localStorage:", parseError);
          }
        } else {
          console.warn("⚠️ task_status_details not found in localStorage");
        }

        // Parse applications from localStorage
        let applicationsData = [];
        if (applicationDetailsStr) {
          try {
            applicationsData = JSON.parse(applicationDetailsStr);
            console.log("✅ Application details loaded from localStorage:", applicationsData.length, "applications");
          } catch (parseError) {
            console.error("❌ Error parsing application_details from localStorage:", parseError);
          }
        } else {
          console.warn("⚠️ application_details not found in localStorage");
        }

        let releaseTypesData = [];
        if (releaseTypeDetailsStr) {
          try {
            releaseTypesData = JSON.parse(releaseTypeDetailsStr);
            console.log("✅ Release type details loaded from localStorage:", releaseTypesData.length, "release types");
          } catch (parseError) {
            console.error("❌ Error parsing release_type_details from localStorage:", parseError);
          }
        } else {
          console.warn("⚠️ release_type_details not found in localStorage");
        }

        let taskTypesData = [];
        if (taskTypeDetailsStr) {
          try {
            taskTypesData = JSON.parse(taskTypeDetailsStr);
            console.log("✅ Task type details loaded from localStorage:", taskTypesData.length, "task types");
          } catch (parseError) {
            console.error("❌ Error parsing task_type_details from localStorage:", parseError);
          }
        } else {
          console.warn("⚠️ task_type_details not found in localStorage");
        }

        let prioritiesData = [];
        if (taskPriorityDetailsStr) {
          try {
            prioritiesData = JSON.parse(taskPriorityDetailsStr);
            console.log(
              "✅ Task priority details loaded from localStorage:",
              Array.isArray(prioritiesData) ? prioritiesData.length : 0,
              "priorities"
            );
          } catch (parseError) {
            console.error("❌ Error parsing task_priority_details from localStorage:", parseError);
          }
        } else {
          console.warn("⚠️ task_priority_details not found in localStorage");
        }

        if (
          (!Array.isArray(prioritiesData) || prioritiesData.length === 0) &&
          !isCancelled &&
          isComponentMounted.current
        ) {
          try {
            const lookup = await TaskService.fetchLookupMasterData(true);
            const fromApi = lookup?.task_priority_details;
            if (Array.isArray(fromApi) && fromApi.length > 0) {
              prioritiesData = fromApi;
              localStorage.setItem("task_priority_details", JSON.stringify(fromApi));
              console.log("✅ Task priority details loaded from fetchLookupMasterData:", fromApi.length);
            }
          } catch (apiErr) {
            console.warn("⚠️ Could not load task_priority_details from API:", apiErr);
          }
        }

        let employeesFormatted = [];
        if (employeeDetailsStr) {
          try {
            const employeesData = JSON.parse(employeeDetailsStr);
            if (Array.isArray(employeesData)) {
              employeesFormatted = employeesData.map((emp) => ({
                id: String(emp.aa_app_employee_uid),
                uid: emp.aa_app_employee_uid,
                employeeId: emp.aa_app_employee_id,
                name: emp.aa_app_employee_name,
                email: emp.aa_app_employee_email,
                department: emp.aa_app_resource_deptname,
                defaultApplicationIds: emp.aa_app_rl_default_application_id,
                roleId: emp.aa_app_rl_role_id,
              }));
              console.log(
                "✅ Employee details loaded from localStorage:",
                employeesFormatted.length,
                "employees"
              );
            }
          } catch (parseError) {
            console.error("❌ Error parsing employee_details from localStorage:", parseError);
          }
        } else {
          console.warn("⚠️ employee_details not found in localStorage");
        }

        if (isCancelled || !isComponentMounted.current) return;

        // Process statuses
        setStatuses(statusesData || []);

        // Process applications with role-based filtering
        const filteredApplications = getFilteredApplications(applicationsData || []);
        console.log("📋 Raw applications from localStorage:", applicationsData?.length || 0);
        console.log("📋 Filtered applications after role-based filtering:", filteredApplications?.length || 0);
        console.log("📋 Filtered applications data:", filteredApplications);
        setApplications(filteredApplications);

        // Normalize selectedApplications to ensure they're numbers and match available applications
        setSelectedApplications(prev => {
          const normalized = prev.map(appId => {
            const numId = typeof appId === 'string' ? parseInt(appId, 10) : appId;
            return isNaN(numId) ? appId : numId;
          });
          
          // Filter out any application IDs that don't exist in the loaded applications
          const validAppIds = filteredApplications.map(app => app.aa_app_rl_application_id);
          return normalized.filter(appId => validAppIds.includes(appId));
        });

        setReleaseTypes(releaseTypesData || []);
        setTaskTypes(taskTypesData || []);

        const sortedPriorities = (Array.isArray(prioritiesData) ? prioritiesData : [])
          .slice()
          .sort(
            (a, b) =>
              (Number(a?.aa_app_rl_priority_id) || 0) - (Number(b?.aa_app_rl_priority_id) || 0)
          );
        setPriorities(sortedPriorities);

        setSelectedPriorities((prev) => {
          if (!Array.isArray(prev) || prev.length === 0) return prev;
          if (sortedPriorities.length === 0) return prev;
          const validNames = new Set(
            sortedPriorities
              .map((p) => String(p?.aa_app_rl_priority_name || "").trim())
              .filter(Boolean)
          );
          return prev.filter((name) => validNames.has(String(name).trim()));
        });

        setMasterEmployeeList(employeesFormatted);
        setOwnerFilterEmployees(employeesFormatted);

        setSelectedReleaseTypes((prev) => {
          const normalized = prev.map((id) => {
            const numId = typeof id === "string" ? parseInt(id, 10) : id;
            return isNaN(numId) ? id : numId;
          });
          const validIds = (releaseTypesData || []).map((rt) => rt.aa_app_rl_release_type_id);
          return normalized.filter((id) => validIds.includes(id));
        });

        setSelectedTaskTypes((prev) => {
          if (!Array.isArray(prev) || prev.length === 0) return Array.isArray(prev) ? prev : [];
          if (!Array.isArray(taskTypesData) || taskTypesData.length === 0) return prev;

          const norm = (v) =>
            String(v || "")
              .toLowerCase()
              .replace(/[\s_-]/g, "")
              .trim();

          const lookup = taskTypesData.map((tt) => ({
            shortName: String(tt?.aa_app_rl_task_type_shortname || "").trim(),
            longName: String(tt?.aa_app_rl_task_type_longname || "").trim(),
          }));

          return prev.map((value) => {
            const n = norm(value);
            const match = lookup.find((it) => {
              const s = norm(it.shortName);
              const l = norm(it.longName);
              if (!s && !l) return false;
              return n === s || n === l || (s && n.includes(s)) || (l && n.includes(l));
            });
            // Keep original if no mapping found, so preset chips are not lost in UI.
            return match ? (match.shortName || match.longName || value) : value;
          });
        });

        initialLoadComplete.current = true;
        setStaticDataLoaded(true);
        console.log("🎉 Initial static data load completed successfully from localStorage");

      } catch (err) {
        if (!isCancelled && isComponentMounted.current) {
          console.error("❌ Error loading initial static data from localStorage:", err);
          setError("Failed to load data from localStorage. Please refresh the page.");
          setLoading(false);
        }
      }
    };

    void loadInitialStaticData();

    return () => {
      isCancelled = true;
    };
  }, [getFilteredApplications]); // Only depends on getFilteredApplications

  // Helper function to map column names to API field names
  const mapColumnToApiField = useCallback((columnName) => {
    const columnMap = {
      'id': 'aa_app_rl_resource_task_uid',
      'title': 'aa_app_rl_resource_task_title',
      'priority': 'aa_app_rl_priority_name',
      'type': 'aa_app_rl_task_type_shortname',
      'application': 'aa_app_rl_application_name',
      'assignedTo': 'aa_app_employee_name',
      'taskOwner': 'aa_app_task_owner_name',
      'status': 'aa_app_rl_task_status_name',
      'createdOn': 'aa_app_task_creation_date',
      'lastUpdatedOn': 'aa_app_task_updation_date',
      'releaseType': 'aa_app_rl_release_type_name',
      'releaseDate': 'aa_app_rl_release_date'
    };
    return columnMap[columnName] || columnName;
  }, []);

  // Function to fetch tasks with current filters (called on button click or initial load)
  const fetchTasksWithFilters = useCallback(async (forceRefresh = false) => {
    // Don't fetch until initial static data is loaded
    if (!initialLoadComplete.current) return;

    try {
      setLoading(true);
      setError(null);
      setNoDataFound(false);
      
      const isAdvanced = searchMode === "advanced";

      console.log("🔍 Fetching tasks with filters:", {
        selectedStatuses,
        selectedPriorities,
        selectedApplications,
        selectedReleaseTypes,
        selectedTaskTypes,
        selectedAgingBuckets,
        searchTerm,
        ownershipFilter,
        searchMode,
        columnSorts,
        forceRefresh
      });

      // Use current page as offset (page 1 = offset 1, page 2 = offset 2, etc.)
      const offset = currentPage;
      
      // Build sorting parameters if column sorts are active
      // Support multiple column sorting by sending comma-separated values
      let sortBy = null;
      let sortOrder = null;
      if (columnSorts.length > 0) {
        // Map all sort columns to API field names and collect directions
        const sortColumns = columnSorts.map(sort => mapColumnToApiField(sort.column));
        const sortDirections = columnSorts.map(sort => sort.direction);
        
        // Join with commas for multiple sorting
        sortBy = sortColumns.join(',');
        sortOrder = sortDirections.join(',');
        
        console.log("🔀 Multiple column sorting:", {
          columns: sortColumns,
          directions: sortDirections,
          sortBy: sortBy,
          sortOrder: sortOrder
        });
      }
      
      // Build filter object for API call
      // Ensure searchTerm is always included (even if empty string)
      const filters = {
        statuses: selectedStatuses,
        priorities: isAdvanced ? selectedPriorities : [],
        applicationIds: selectedApplications,
        taskTypes: Array.isArray(selectedTaskTypes) && selectedTaskTypes.length > 0 ? selectedTaskTypes : [],
        agingBuckets:
          isAdvanced && Array.isArray(selectedAgingBuckets) && selectedAgingBuckets.length > 0
            ? selectedAgingBuckets
            : [],
        ...(internalOverdueClientFilter ? { overdueTasks: true } : {}),
        ...(internalUnassignedClientFilter ? { unassignedTasks: true } : {}),
        releaseTypeIds: isAdvanced ? selectedReleaseTypes : [],
        taskOwnerIds: isAdvanced ? selectedTaskOwnerIds : [],
        createdByIds: isAdvanced ? selectedCreatedByIds : [],
        searchTerm: searchTerm || '', // Always include searchTerm, even if empty
        ownershipFilter: ownershipFilter,
        offset: offset,
        limit: itemsPerPage,
        sortBy: sortBy,
        sortOrder: sortOrder
      };

      // Log the complete filters object to verify searchTerm is included
      console.log("📋 Complete filters object being sent:", JSON.stringify(filters, null, 2));
      console.log("🔍 Search term value:", filters.searchTerm, "Type:", typeof filters.searchTerm, "Length:", filters.searchTerm?.length);

      // Fetch tasks with filters - use forceRefresh to bypass cache when Search button is clicked
      const response = await TaskService.getAllTasks(filters, forceRefresh);

      if (!isComponentMounted.current) return;

      // Handle response structure - could be array or object with data property
      let tasksData;
      if (Array.isArray(response)) {
        tasksData = response;
        setTotalCount(response.length); // Fallback if no total_count
      } else if (response && response.data) {
        tasksData = response.data;
        setTotalCount(response.total_count || response.data.length);
      } else {
        tasksData = [];
        setTotalCount(0);
      }

      // Transform tasks
      let transformedTasks = transformTasks(tasksData);

      // Fallback: narrow by release type when IDs are selected (in case API ignores release_type_ids until backend supports it)
      if (isAdvanced && selectedReleaseTypes.length > 0) {
        const idSet = new Set(
          selectedReleaseTypes.map((id) => {
            const n = typeof id === "string" ? parseInt(id, 10) : id;
            return isNaN(n) ? id : n;
          })
        );
        transformedTasks = transformedTasks.filter((t) => {
          const tid =
            t.releaseTypeId != null && !Number.isNaN(Number(t.releaseTypeId))
              ? Number(t.releaseTypeId)
              : null;
          if (tid == null) return false;
          return idSet.has(tid);
        });
      }

      // Task owner: refine result set (API also receives task_owner_ids). Support legacy non-numeric tokens as name substring.
      if (isAdvanced && selectedTaskOwnerIds.length > 0) {
        const numericIds = selectedTaskOwnerIds.filter((id) => /^\d+$/.test(String(id)));
        const legacyText = selectedTaskOwnerIds.filter((id) => !/^\d+$/.test(String(id)));
        if (numericIds.length > 0) {
          const idSet = new Set(numericIds.map((id) => String(id)));
          transformedTasks = transformedTasks.filter((task) =>
            idSet.has(String(task.ownerId ?? ""))
          );
        }
        if (legacyText.length > 0) {
          transformedTasks = transformedTasks.filter((task) => {
            const name = (task.ownerName || "").toLowerCase();
            return legacyText.some((token) => name.includes(String(token).toLowerCase()));
          });
        }
      }

      // Created by: rely on API (created_by_ids) only — list rows often omit aa_app_task_created_by,
      // so a client-side filter here would hide all results even when the backend returned matches.

      // Release date: apply only after Search (fetch), not while changing the date input
      if (isAdvanced && releaseDateFilter) {
        transformedTasks = transformedTasks.filter(
          (task) => task.releaseDateCompare === releaseDateFilter
        );
      }

      // Age bucket: refine by ticket_age_days when API ignores aging_buckets or returns broader set
      if (isAdvanced && selectedAgingBuckets.length > 0) {
        transformedTasks = transformedTasks.filter((task) =>
          taskMatchesAgingBuckets(task.ticketAgeDays, selectedAgingBuckets)
        );
      }

      // Extract unique lead names from fetched tasks
      const uniqueLeadNames = Array.from(
        new Set(
          transformedTasks
            .filter(task => task.leadName)
            .map(task => task.leadName)
        )
      );
      
      setLeadNames(uniqueLeadNames);

      // Only apply default sorting if no server-side sorting is active
      // If server-side sorting is active, backend already sorted the data
      let sortedTasks = transformedTasks;
      if (columnSorts.length === 0) {
        // Apply default sorting when no column sorts are active
        sortedTasks = transformedTasks.sort((a, b) => {
          const dateA = a.updationDate && a.updationDate !== "N/A" ? new Date(a.updationDate) : new Date(0);
          const dateB = b.updationDate && b.updationDate !== "N/A" ? new Date(b.updationDate) : new Date(0);
          
          if (dateA.getTime() !== dateB.getTime()) {
            return dateB - dateA;
          }
          
          const statusPriorityA = getStatusPriority(a.status);
          const statusPriorityB = getStatusPriority(b.status);
          
          return statusPriorityA - statusPriorityB;
        });
      }

      setAllTasks(sortedTasks);
      setLoading(false);

      console.log("✅ Tasks fetched successfully with filters, count:", sortedTasks.length);

    } catch (err) {
      if (isComponentMounted.current) {
        console.error("❌ Error fetching tasks with filters:", err);
        
        // Check if error is 404 (no data found)
        if (err.response && err.response.status === 404) {
          setNoDataFound(true);
          setError(null);
          setAllTasks([]);
        } else {
          setError("Failed to load tasks. Please try again later.");
          setNoDataFound(false);
        }
        setLoading(false);
      }
    }
  }, [
    selectedStatuses,
    selectedPriorities,
    selectedApplications,
    selectedReleaseTypes,
    selectedTaskTypes,
    selectedAgingBuckets,
    searchTerm,
    ownershipFilter,
    searchMode,
    selectedTaskOwnerIds,
    selectedCreatedByIds,
    internalOverdueClientFilter,
    internalUnassignedClientFilter,
    releaseDateFilter,
    transformTasks,
    getStatusPriority,
    currentPage,
    itemsPerPage,
    columnSorts,
    mapColumnToApiField
  ]);

  // Update ref whenever fetchTasksWithFilters changes
  useEffect(() => {
    fetchTasksRef.current = fetchTasksWithFilters;
  }, [fetchTasksWithFilters]);

  // Function to fetch tasks with initial load filters (In Progress, Not started, Assigned)
  const fetchInitialTasks = useCallback(async () => {
    // Don't fetch until initial static data is loaded
    if (!initialLoadComplete.current) return;

    try {
      setLoading(true);
      setError(null);
      
      // Get exact status names from API (statuses should be loaded by now)
      let initialStatuses = ["In Progress", "Not started"];
      if (statuses.length > 0) {
        // Find status objects by their IDs (1 = Not started, 2 = In Progress)
        const notStartedStatus = statuses.find(s => s.aa_app_rl_task_status_id === 1);
        const inProgressStatus = statuses.find(s => s.aa_app_rl_task_status_id === 2);
        
        // Use exact API names if available
        if (notStartedStatus?.aa_app_rl_task_status_name && inProgressStatus?.aa_app_rl_task_status_name) {
          initialStatuses = [
            inProgressStatus.aa_app_rl_task_status_name,
            notStartedStatus.aa_app_rl_task_status_name
          ];
        }
      }
      
      // Use hardcoded initial filters: In Progress, Not started, and Assigned
      const initialFilters = {
        statuses: initialStatuses,
        priorities: [],
        applicationIds: [],
        taskTypes: [],
        releaseTypeIds: [],
        taskOwnerIds: [],
        createdByIds: [],
        searchTerm: "",
        ownershipFilter: "Assigned",
        offset: 1,
        limit: itemsPerPage
      };
      
      console.log("🔍 Fetching initial tasks with hardcoded filters:", initialFilters);

      // Fetch tasks with initial filters - force refresh to always get latest data
      const response = await TaskService.getAllTasks(initialFilters, true);

      if (!isComponentMounted.current) return;

      // Handle response structure - could be array or object with data property
      let tasksData;
      if (Array.isArray(response)) {
        tasksData = response;
        setTotalCount(response.length); // Fallback if no total_count
      } else if (response && response.data) {
        tasksData = response.data;
        setTotalCount(response.total_count || response.data.length);
      } else {
        tasksData = [];
        setTotalCount(0);
      }

      // Transform tasks
      const transformedTasks = transformTasks(tasksData);

      // Extract unique lead names from fetched tasks
      const uniqueLeadNames = Array.from(
        new Set(
          transformedTasks
            .filter(task => task.leadName)
            .map(task => task.leadName)
        )
      );
      
      setLeadNames(uniqueLeadNames);

      // Apply default sorting
      const sortedTasks = transformedTasks.sort((a, b) => {
        const dateA = a.updationDate && a.updationDate !== "N/A" ? new Date(a.updationDate) : new Date(0);
        const dateB = b.updationDate && b.updationDate !== "N/A" ? new Date(b.updationDate) : new Date(0);
        
        if (dateA.getTime() !== dateB.getTime()) {
          return dateB - dateA;
        }
        
        const statusPriorityA = getStatusPriority(a.status);
        const statusPriorityB = getStatusPriority(b.status);
        
        return statusPriorityA - statusPriorityB;
      });

      setAllTasks(sortedTasks);
      setLoading(false);

      console.log("✅ Initial tasks fetched successfully, count:", sortedTasks.length);

    } catch (err) {
      if (isComponentMounted.current) {
        console.error("❌ Error fetching initial tasks:", err);
        setError("Failed to load tasks. Please try again later.");
        setLoading(false);
      }
    }
  }, [transformTasks, getStatusPriority, statuses, itemsPerPage]);

  // Effect to fetch tasks on initial mount only (with hardcoded initial filters)
  useEffect(() => {
    // Skip initial fetch if we're restoring filters from navigation
    if (!staticDataLoaded || statuses.length === 0 || initialTaskFetchDone.current || filtersRestoredFromNavigation.current) return;

    if (fromInternalPresetRef.current) {
      initialTaskFetchDone.current = true;
      fetchTasksWithFilters(true);
      return;
    }

    const trimmedSearch = String(searchTerm || "").trim();
    if (trimmedSearch || filtersLoadedFromStorage.current) {
      initialTaskFetchDone.current = true;
      fetchTasksWithFilters(true);
      return;
    }

    fetchInitialTasks();
    initialTaskFetchDone.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staticDataLoaded, statuses.length]); // Only run once when static data and statuses are loaded

  useEffect(() => {
    if (location.state?.from === "internal-dashboard") {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state?.from, navigate]);
  
  // Effect to fetch tasks when page changes (but not on initial mount)
  useEffect(() => {
    if (!initialLoadComplete.current || initialTaskFetchDone.current === false) return;
    
    // Only fetch if we're not on the initial load
    fetchTasksWithFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]); // Fetch when page changes

  // Cleanup effect
  useEffect(() => {
    return () => {
      isComponentMounted.current = false;
    };
  }, []);

  // Restore filters from location.state when coming back from create/update pages
  useEffect(() => {
    if (location.state?.preserveFilters && location.state?.filters && !filtersRestoredFromNavigation.current) {
      const restoredFilters = location.state.filters;
      console.log("🔄 Restoring filters from navigation state:", restoredFilters);
      
      // Mark that we're restoring filters to prevent multiple restorations
      filtersRestoredFromNavigation.current = true;
      
      // Restore all filter states
      if (restoredFilters.selectedStatuses) {
        setSelectedStatuses(restoredFilters.selectedStatuses);
      }
      if (restoredFilters.selectedPriorities) {
        setSelectedPriorities(restoredFilters.selectedPriorities);
      }
      if (restoredFilters.selectedApplications) {
        setSelectedApplications(restoredFilters.selectedApplications);
      }
      if (restoredFilters.selectedReleaseTypes) {
        setSelectedReleaseTypes(restoredFilters.selectedReleaseTypes);
      }
      if (restoredFilters.searchTerm !== undefined) {
        setSearchTerm(restoredFilters.searchTerm);
      }
      if (restoredFilters.searchMode === "basic" || restoredFilters.searchMode === "advanced") {
        setSearchMode(restoredFilters.searchMode);
      }
      if (restoredFilters.searchPanelExpanded !== undefined) {
        setSearchPanelExpanded(!!restoredFilters.searchPanelExpanded);
      }
      if (
        restoredFilters.selectedTaskOwnerIds !== undefined ||
        restoredFilters.taskOwnerFilter !== undefined
      ) {
        setSelectedTaskOwnerIds(
          normalizeSavedTaskOwnerIds(
            restoredFilters.selectedTaskOwnerIds ?? restoredFilters.taskOwnerFilter
          )
        );
      }
      if (restoredFilters.releaseDateFilter !== undefined) {
        setReleaseDateFilter(String(restoredFilters.releaseDateFilter || ""));
      }
      if (Array.isArray(restoredFilters.selectedCreatedByIds)) {
        setSelectedCreatedByIds(
          restoredFilters.selectedCreatedByIds.map((id) => String(id).trim()).filter(Boolean)
        );
      }
      if (Array.isArray(restoredFilters.selectedTaskTypes)) {
        setSelectedTaskTypes(restoredFilters.selectedTaskTypes);
      }
      if (Array.isArray(restoredFilters.selectedAgingBuckets)) {
        setSelectedAgingBuckets(restoredFilters.selectedAgingBuckets);
      }
      if (restoredFilters.internalOverdueClientFilter !== undefined) {
        setInternalOverdueClientFilter(!!restoredFilters.internalOverdueClientFilter);
      }
      if (restoredFilters.internalUnassignedClientFilter !== undefined) {
        setInternalUnassignedClientFilter(!!restoredFilters.internalUnassignedClientFilter);
      }
      if (restoredFilters.ownershipFilter) {
        // Always use "Assigned" instead of "TaskOwner"
        const ownershipValue = restoredFilters.ownershipFilter === "TaskOwner" 
          ? "Assigned" 
          : restoredFilters.ownershipFilter;
        setOwnershipFilter(ownershipValue);
      } else {
        setOwnershipFilter(getDefaultOwnershipFilter());
      }
      
      // Clear the location state to prevent re-applying on re-renders
      window.history.replaceState({}, document.title);
      
      // Automatically fetch with restored filters after state is updated
      // This ensures the data matches the restored filters
      if (initialLoadComplete.current) {
        setTimeout(() => {
          const restoredAdvanced = restoredFilters.searchMode === "advanced";
          const restoredOwnerIds = normalizeSavedTaskOwnerIds(
            restoredFilters.selectedTaskOwnerIds ?? restoredFilters.taskOwnerFilter
          );
          const restoredCreatedByIds = Array.isArray(restoredFilters.selectedCreatedByIds)
            ? restoredFilters.selectedCreatedByIds.map((id) => String(id).trim()).filter(Boolean)
            : [];
          const filtersToUse = {
            statuses: restoredFilters.selectedStatuses || [],
            priorities: restoredAdvanced ? (restoredFilters.selectedPriorities || []) : [],
            applicationIds: restoredFilters.selectedApplications || [],
            taskTypes: Array.isArray(restoredFilters.selectedTaskTypes)
              ? restoredFilters.selectedTaskTypes
              : [],
            agingBuckets:
              restoredAdvanced && Array.isArray(restoredFilters.selectedAgingBuckets)
                ? restoredFilters.selectedAgingBuckets
                : [],
            releaseTypeIds: restoredAdvanced ? (restoredFilters.selectedReleaseTypes || []) : [],
            taskOwnerIds: restoredAdvanced ? restoredOwnerIds : [],
            createdByIds: restoredAdvanced ? restoredCreatedByIds : [],
            ...(restoredFilters.internalOverdueClientFilter ? { overdueTasks: true } : {}),
            ...(restoredFilters.internalUnassignedClientFilter ? { unassignedTasks: true } : {}),
            searchTerm: restoredFilters.searchTerm || "",
            ownershipFilter: (restoredFilters.ownershipFilter && restoredFilters.ownershipFilter !== "TaskOwner") 
              ? restoredFilters.ownershipFilter 
              : getDefaultOwnershipFilter(),
            offset: 1,
            limit: itemsPerPage
          };
          console.log("🔍 Auto-fetching with restored filters:", filtersToUse);
          
          // Use TaskService directly with restored filters to ensure correct data
          TaskService.getAllTasks(filtersToUse, true).then(response => {
            if (!isComponentMounted.current) return;
            
            let tasksData;
            if (Array.isArray(response)) {
              tasksData = response;
              setTotalCount(response.length);
            } else if (response && response.data) {
              tasksData = response.data;
              setTotalCount(response.total_count || response.data.length);
            } else {
              tasksData = [];
              setTotalCount(0);
            }
            
            let transformedTasks = transformTasks(tasksData);

            const releaseIds = restoredFilters.selectedReleaseTypes || [];
            if (restoredAdvanced && releaseIds.length > 0) {
              const idSet = new Set(
                releaseIds.map((id) => {
                  const n = typeof id === "string" ? parseInt(id, 10) : id;
                  return isNaN(n) ? id : n;
                })
              );
              transformedTasks = transformedTasks.filter((t) => {
                const tid =
                  t.releaseTypeId != null && !Number.isNaN(Number(t.releaseTypeId))
                    ? Number(t.releaseTypeId)
                    : null;
                if (tid == null) return false;
                return idSet.has(tid);
              });
            }

            if (restoredAdvanced && restoredOwnerIds.length > 0) {
              const numericIds = restoredOwnerIds.filter((id) => /^\d+$/.test(String(id)));
              const legacyText = restoredOwnerIds.filter((id) => !/^\d+$/.test(String(id)));
              if (numericIds.length > 0) {
                const ownerSet = new Set(numericIds.map((id) => String(id)));
                transformedTasks = transformedTasks.filter((t) =>
                  ownerSet.has(String(t.ownerId ?? ""))
                );
              }
              if (legacyText.length > 0) {
                transformedTasks = transformedTasks.filter((t) => {
                  const name = (t.ownerName || "").toLowerCase();
                  return legacyText.some((token) => name.includes(String(token).toLowerCase()));
                });
              }
            }

            // Created by: server-filtered only (see fetchTasksWithFilters).

            const restoredReleaseDate =
              typeof restoredFilters.releaseDateFilter === "string"
                ? restoredFilters.releaseDateFilter
                : "";
            if (restoredAdvanced && restoredReleaseDate) {
              transformedTasks = transformedTasks.filter(
                (t) => t.releaseDateCompare === restoredReleaseDate
              );
            }

            const restoredAgingBuckets = Array.isArray(restoredFilters.selectedAgingBuckets)
              ? restoredFilters.selectedAgingBuckets
              : [];
            if (restoredAdvanced && restoredAgingBuckets.length > 0) {
              transformedTasks = transformedTasks.filter((t) =>
                taskMatchesAgingBuckets(t.ticketAgeDays, restoredAgingBuckets)
              );
            }
            
            const uniqueLeadNames = Array.from(
              new Set(
                transformedTasks
                  .filter(task => task.leadName)
                  .map(task => task.leadName)
              )
            );
            setLeadNames(uniqueLeadNames);
            
            const sortedTasks = transformedTasks.sort((a, b) => {
              const dateA = a.updationDate && a.updationDate !== "N/A" ? new Date(a.updationDate) : new Date(0);
              const dateB = b.updationDate && b.updationDate !== "N/A" ? new Date(b.updationDate) : new Date(0);
              
              if (dateA.getTime() !== dateB.getTime()) {
                return dateB - dateA;
              }
              
              const statusPriorityA = getStatusPriority(a.status);
              const statusPriorityB = getStatusPriority(b.status);
              
              return statusPriorityA - statusPriorityB;
            });
            
            setAllTasks(sortedTasks);
            setLoading(false);
            initialTaskFetchDone.current = true; // Mark that initial fetch is done
            console.log("✅ Tasks fetched successfully with restored filters, count:", sortedTasks.length);
          }).catch(err => {
            if (isComponentMounted.current) {
              console.error("❌ Error fetching tasks with restored filters:", err);
              if (err.response && err.response.status === 404) {
                setNoDataFound(true);
                setError(null);
                setAllTasks([]);
              } else {
                setError("Failed to load tasks. Please try again later.");
                setNoDataFound(false);
              }
              setLoading(false);
              initialTaskFetchDone.current = true; // Mark that initial fetch is done even on error
            }
          });
        }, 100);
      }
    }
    
    // Reset the flag when location.state changes (user navigates away and comes back)
    if (!location.state?.preserveFilters) {
      filtersRestoredFromNavigation.current = false;
    }
  }, [location.state, getDefaultOwnershipFilter, itemsPerPage, transformTasks, getStatusPriority]);

  // Update default statuses after statuses are loaded to use exact API names
  // This converts default string values ("In Progress", "Not started") to exact API names
  // It should run for both default values and saved filters that still use default strings
  useEffect(() => {
    // Don't run if:
    // 1. Statuses aren't loaded yet
    // 2. We've already updated default statuses
    // 3. Filters were restored from navigation (they're already correct)
    if (statuses.length > 0 && !defaultStatusesUpdated.current && !filtersRestoredFromNavigation.current) {
      // Find status objects by their IDs (1 = Not started, 2 = In Progress)
      const notStartedStatus = statuses.find(s => s.aa_app_rl_task_status_id === 1);
      const inProgressStatus = statuses.find(s => s.aa_app_rl_task_status_id === 2);
      
      // Get the exact status names from API
      const notStartedName = notStartedStatus?.aa_app_rl_task_status_name;
      const inProgressName = inProgressStatus?.aa_app_rl_task_status_name;
      
      if (notStartedName && inProgressName) {
        // Use a function to get current state to avoid dependency issues
        setSelectedStatuses(prevStatuses => {
          // Check if we're using the default string values
          const hasInProgressDefault = prevStatuses.includes("In Progress");
          const hasNotStartedDefault = prevStatuses.includes("Not started");
          
          // Check if we already have API names (means filters were saved with API names)
          const hasInProgressApi = prevStatuses.includes(inProgressName);
          const hasNotStartedApi = prevStatuses.includes(notStartedName);
          
          // Only update if we have default string values AND we don't already have the corresponding API names
          // This handles both cases:
          // 1. Default values that need conversion
          // 2. Saved filters with default strings that need conversion
          // But NOT saved filters that already have API names
          if ((hasInProgressDefault && !hasInProgressApi) || (hasNotStartedDefault && !hasNotStartedApi)) {
            // Build updated statuses array - preserve all existing statuses
            const updatedStatuses = [...prevStatuses];
            
            // Track if we had both default statuses originally (to ensure we preserve both)
            const hadBothDefaults = hasInProgressDefault && hasNotStartedDefault;
            
            // Replace "In Progress" with exact API name (if it exists and API name not already present)
            if (hasInProgressDefault && !hasInProgressApi) {
              const index = updatedStatuses.indexOf("In Progress");
              if (index !== -1) {
                updatedStatuses[index] = inProgressName;
              } else if (!updatedStatuses.includes(inProgressName)) {
                updatedStatuses.push(inProgressName);
              }
            }
            
            // Replace "Not started" with exact API name (if it exists and API name not already present)
            if (hasNotStartedDefault && !hasNotStartedApi) {
              const index = updatedStatuses.indexOf("Not started");
              if (index !== -1) {
                updatedStatuses[index] = notStartedName;
              } else if (!updatedStatuses.includes(notStartedName)) {
                updatedStatuses.push(notStartedName);
              }
            }
            
            // Safeguard: If we had both default statuses, ensure both API names are present
            if (hadBothDefaults) {
              if (!updatedStatuses.includes(inProgressName)) {
                updatedStatuses.push(inProgressName);
              }
              if (!updatedStatuses.includes(notStartedName)) {
                updatedStatuses.push(notStartedName);
              }
            }
            
            // Remove duplicates while preserving order
            const uniqueStatuses = Array.from(new Set(updatedStatuses));
            
            defaultStatusesUpdated.current = true;
            console.log("🔄 Updated statuses from strings to API names:", {
              before: prevStatuses,
              after: uniqueStatuses,
              loadedFromStorage: filtersLoadedFromStorage.current,
              hadBothDefaults
            });
            return uniqueStatuses;
          }
          
          // If we already have API names or no default strings, mark as updated to prevent further changes
          defaultStatusesUpdated.current = true;
          if (filtersLoadedFromStorage.current) {
            console.log("✅ Filters from localStorage already have API names - no conversion needed");
          }
          
          return prevStatuses;
        });
      } else {
        // If we can't find the status names, mark as updated anyway to prevent infinite loops
        defaultStatusesUpdated.current = true;
      }
    }
  }, [statuses]); // Only run when statuses are loaded

  // Memoized handler functions for multi-select
  const handleStatusToggle = useCallback((statusName) => {
    setSelectedStatuses(prev => {
      if (prev.includes(statusName)) {
        return prev.filter(s => s !== statusName);
      } else {
        return [...prev, statusName];
      }
    });
  }, []);

  const handlePriorityToggle = useCallback((priority) => {
    setSelectedPriorities(prev => {
      if (prev.includes(priority)) {
        return prev.filter(p => p !== priority);
      } else {
        return [...prev, priority];
      }
    });
  }, []);

  const handleTaskTypeToggle = useCallback((taskTypeValue) => {
    if (!taskTypeValue) return;
    const normalized = String(taskTypeValue).trim();
    if (!normalized) return;
    setSelectedTaskTypes((prev) => {
      if (prev.includes(normalized)) {
        return prev.filter((t) => t !== normalized);
      }
      return [...prev, normalized];
    });
  }, []);

  const handleAgingBucketToggle = useCallback((bucketCode) => {
    if (!bucketCode) return;
    const normalized = String(bucketCode).trim();
    if (!normalized) return;
    setSelectedAgingBuckets((prev) => {
      if (prev.includes(normalized)) {
        return prev.filter((b) => b !== normalized);
      }
      return [...prev, normalized];
    });
  }, []);

  const loadAgingChartData = useCallback(async () => {
    setAgingChartLoading(true);
    setAgingChartError(null);
    try {
      const isAdvanced = searchMode === "advanced";
      const filters = {
        statuses: selectedStatuses,
        priorities: isAdvanced ? selectedPriorities : [],
        applicationIds: selectedApplications,
        taskTypes:
          Array.isArray(selectedTaskTypes) && selectedTaskTypes.length > 0
            ? selectedTaskTypes
            : [],
        ...(internalOverdueClientFilter ? { overdueTasks: true } : {}),
        ...(internalUnassignedClientFilter ? { unassignedTasks: true } : {}),
        releaseTypeIds: isAdvanced ? selectedReleaseTypes : [],
        taskOwnerIds: isAdvanced ? selectedTaskOwnerIds : [],
        createdByIds: isAdvanced ? selectedCreatedByIds : [],
        searchTerm: searchTerm || "",
        ownershipFilter,
      };

      const summary = await TaskService.fetchTicketAgingReport(filters, true);
      const { totals, totalTickets } = aggregateAgingBucketTotalsFromSummary(
        Array.isArray(summary) ? summary : []
      );

      if (totalTickets > 0) {
        setAgingChartTotals(totals);
        setAgingChartTotalTickets(totalTickets);
      } else {
        // Fallback: current page tasks when summary is empty
        const fromTasks = aggregateAgingBucketTotalsFromTasks(allTasks);
        setAgingChartTotals(fromTasks.totals);
        setAgingChartTotalTickets(fromTasks.totalTickets);
      }
    } catch (err) {
      console.error("Error loading ageing chart for All Tasks search:", err);
      const fromTasks = aggregateAgingBucketTotalsFromTasks(allTasks);
      if (fromTasks.totalTickets > 0) {
        setAgingChartTotals(fromTasks.totals);
        setAgingChartTotalTickets(fromTasks.totalTickets);
        setAgingChartError(null);
      } else {
        setAgingChartTotals(emptyAgingBucketTotals());
        setAgingChartTotalTickets(0);
        setAgingChartError("Could not load ageing counts.");
      }
    } finally {
      setAgingChartLoading(false);
    }
  }, [
    searchMode,
    selectedStatuses,
    selectedPriorities,
    selectedApplications,
    selectedTaskTypes,
    internalOverdueClientFilter,
    internalUnassignedClientFilter,
    selectedReleaseTypes,
    selectedTaskOwnerIds,
    selectedCreatedByIds,
    searchTerm,
    ownershipFilter,
    allTasks,
  ]);

  const handleToggleAgingChart = useCallback(() => {
    setAgingChartOpen((prev) => !prev);
  }, []);

  const handleAgingChartBucketClick = useCallback((bucketCode) => {
    if (!bucketCode) return;
    pendingAgingChartSearchRef.current = true;
    setSearchMode("advanced");
    setSearchPanelExpanded(true);
    setSelectedAgingBuckets([bucketCode]);
    setAgingChartOpen(false);
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    if (!pendingAgingChartSearchRef.current) return;
    pendingAgingChartSearchRef.current = false;
    fetchTasksWithFilters(true);
  }, [selectedAgingBuckets, searchMode, fetchTasksWithFilters]);

  useEffect(() => {
    if (!agingChartOpen) return;
    loadAgingChartData();
  }, [agingChartOpen, loadAgingChartData]);

  useEffect(() => {
    if (!agingChartOpen) return undefined;
    const handleOutside = (event) => {
      if (
        agingChartWrapRef.current &&
        !agingChartWrapRef.current.contains(event.target)
      ) {
        setAgingChartOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setAgingChartOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [agingChartOpen]);

  const agingChartDonutGradient = useMemo(
    () => buildAgingDonutGradient(agingChartTotals, agingChartTotalTickets),
    [agingChartTotals, agingChartTotalTickets]
  );

  const handleReleaseTypeToggle = useCallback((releaseTypeId) => {
    setSelectedReleaseTypes((prev) => {
      const idNum = typeof releaseTypeId === "string" ? parseInt(releaseTypeId, 10) : releaseTypeId;
      const normalizedId = isNaN(idNum) ? releaseTypeId : idNum;
      const normalizedPrev = prev.map((id) => {
        const n = typeof id === "string" ? parseInt(id, 10) : id;
        return isNaN(n) ? id : n;
      });
      if (normalizedPrev.includes(normalizedId)) {
        return normalizedPrev.filter((a) => a !== normalizedId);
      }
      return [...normalizedPrev, normalizedId];
    });
  }, []);

  const handleOverdueOnlyToggle = useCallback((e) => {
    setInternalOverdueClientFilter(!!e.target.checked);
  }, []);

  const handleUnassignedOnlyToggle = useCallback((e) => {
    setInternalUnassignedClientFilter(!!e.target.checked);
  }, []);

  const handleTaskOwnerToggle = useCallback((ownerId) => {
    if (ownerId == null || String(ownerId).trim() === "") return;
    const id = String(ownerId).trim();
    setSelectedTaskOwnerIds((prev) => {
      const prevStr = prev.map((x) => String(x));
      if (prevStr.includes(id)) {
        return prev.filter((x) => String(x) !== id);
      }
      return [...prevStr, id];
    });
  }, []);

  const handleCreatedByToggle = useCallback((employeeUid) => {
    if (employeeUid == null || String(employeeUid).trim() === "") return;
    const id = String(employeeUid).trim();
    setSelectedCreatedByIds((prev) => {
      const prevStr = prev.map((x) => String(x));
      if (prevStr.includes(id)) {
        return prev.filter((x) => String(x) !== id);
      }
      return [...prevStr, id];
    });
  }, []);

  const handleApplicationToggle = useCallback((appId) => {
    setSelectedApplications(prev => {
      // Ensure appId is a number for consistent comparison
      const appIdNum = typeof appId === 'string' ? parseInt(appId, 10) : appId;
      const normalizedAppId = isNaN(appIdNum) ? appId : appIdNum;
      
      // Normalize prev array to numbers for comparison
      const normalizedPrev = prev.map(id => {
        const numId = typeof id === 'string' ? parseInt(id, 10) : id;
        return isNaN(numId) ? id : numId;
      });
      
      if (normalizedPrev.includes(normalizedAppId)) {
        return normalizedPrev.filter(a => a !== normalizedAppId);
      } else {
        return [...normalizedPrev, normalizedAppId];
      }
    });
  }, []);

  // Handler for column sorting - triggers service call
  const handleColumnSort = useCallback((columnName) => {
    setColumnSorts(prevSorts => {
      const existingSortIndex = prevSorts.findIndex(sort => sort.column === columnName);
      let newSorts;
      
      if (existingSortIndex >= 0) {
        const existingSort = prevSorts[existingSortIndex];
        // Toggle direction: asc -> desc -> remove
        if (existingSort.direction === 'asc') {
          // Change to desc
          newSorts = [...prevSorts];
          newSorts[existingSortIndex] = { ...existingSort, direction: 'desc' };
        } else {
          // Remove from sorts
          newSorts = prevSorts.filter((_, index) => index !== existingSortIndex);
        }
      } else {
        // Add new sort with asc direction
        newSorts = [...prevSorts, { column: columnName, direction: 'asc' }];
      }
      
      // Reset to page 1 when sorting changes
      setCurrentPage(1);
      
      // Trigger fetch with new sorting (use ref to avoid dependency issues)
      setTimeout(() => {
        if (fetchTasksRef.current) {
          fetchTasksRef.current(true);
        }
      }, 0);
      
      return newSorts;
    });
  }, []);

  // Helper function to get sort indicator for a column
  const getSortIndicator = useCallback((columnName) => {
    const sort = columnSorts.find(s => s.column === columnName);
    if (!sort) return null;
    
    return (
      <span className="sort-indicator" style={{ marginLeft: '4px', fontSize: '9px' }}>
        {sort.direction === 'asc' ? '↑' : '↓'}
      </span>
    );
  }, [columnSorts]);

  // Helper function to get sort order number for a column
  const getSortOrder = useCallback((columnName) => {
    const sortIndex = columnSorts.findIndex(s => s.column === columnName);
    if (sortIndex < 0) return null;
    
    return (
      <span className="sort-order" style={{ 
        marginLeft: '2px', 
        fontSize: '8px', 
        color: '#666',
        fontWeight: 'bold'
      }}>
        {sortIndex + 1}
      </span>
    );
  }, [columnSorts]);

  // Helper function to parse date string (MM/DD/YYYY format)
  const parseDateString = useCallback((dateStr) => {
    if (!dateStr || dateStr === 'N/A') return null;
    
    // Try parsing as-is first
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return date;
    }
    
    // Try parsing MM/DD/YYYY format
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const month = parseInt(parts[0], 10) - 1; // Month is 0-indexed
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      const parsedDate = new Date(year, month, day);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
      }
    }
    
    return null;
  }, []);

  // Helper function to get sort value for a task based on column
  const getSortValue = useCallback((task, columnName) => {
    switch (columnName) {
      case 'id':
        return parseInt(task.id) || 0;
      case 'title':
        return (task.title || '').toLowerCase();
      case 'priority':
        return Number(task.priorityId) || 0;
      case 'type':
        return (task.taskType || '').toLowerCase();
      case 'application':
        return (task.application || '').toLowerCase();
      case 'assignedTo':
        return (task.employeeName || '').toLowerCase();
      case 'taskOwner':
        return (task.ownerName || '').toLowerCase();
      case 'status':
        return (task.status || '').toLowerCase();
      case 'createdOn':
        if (!task.creationDate || task.creationDate === 'N/A') return new Date(0);
        // Parse date string (format: MM/DD/YYYY)
        const createdDate = parseDateString(task.creationDate);
        return createdDate || new Date(0);
      case 'lastUpdatedOn':
        if (!task.updationDate || task.updationDate === 'N/A') return new Date(0);
        // Parse date string (format: MM/DD/YYYY)
        const updatedDate = parseDateString(task.updationDate);
        return updatedDate || new Date(0);
      case 'releaseType':
        return (task.releaseTypeName || '').toLowerCase();
      case 'releaseDate':
        if (!task.releaseDate || task.releaseDate === 'N/A') return new Date(0);
        const releaseDateParsed = parseDateString(task.releaseDate);
        return releaseDateParsed || new Date(0);
      default:
        return '';
    }
  }, []);

  // Process tasks - no client-side sorting when server-side sorting is active
  const processedTasks = useMemo(() => {
    console.log("🔍 Processing tasks:", {
      totalTasks: allTasks.length,
      columnSorts: columnSorts.length,
      serverSideSorting: columnSorts.length > 0
    });

    let result;
    if (columnSorts.length > 0) {
      result = allTasks;
    } else {
      result = [...allTasks].sort((a, b) => {
        const dateA = a.updationDate && a.updationDate !== "N/A" ? new Date(a.updationDate) : new Date(0);
        const dateB = b.updationDate && b.updationDate !== "N/A" ? new Date(b.updationDate) : new Date(0);

        if (dateA.getTime() !== dateB.getTime()) {
          return dateB - dateA;
        }

        const statusPriorityA = getStatusPriority(a.status);
        const statusPriorityB = getStatusPriority(b.status);

        return statusPriorityA - statusPriorityB;
      });
    }

    // NOTE: overdue filtering is sent to the API only as `overdue_tasks=true` when the toggle is on.
    // Keep server-returned rows intact to avoid hiding valid results client-side.

    console.log(`✅ Processing complete: ${result.length} tasks`);
    return result;
  }, [
    allTasks,
    getStatusPriority,
    columnSorts,
    searchMode,
    internalOverdueClientFilter,
  ]);

  // Memoized navigation handlers
  const handleBack = useCallback(() => {
    navigate("/dashboard");
  }, [navigate]);

  const handleCreateTask = useCallback(() => {
    // Save current filters before navigating
    const filterState = {
      selectedStatuses,
      selectedPriorities,
      selectedApplications,
      selectedReleaseTypes,
      selectedTaskTypes,
      selectedAgingBuckets,
      internalOverdueClientFilter,
      internalUnassignedClientFilter,
      searchTerm,
      ownershipFilter,
      searchMode,
      searchPanelExpanded,
      selectedTaskOwnerIds,
      selectedCreatedByIds,
      releaseDateFilter,
    };
    localStorage.setItem("taskFilters", JSON.stringify(filterState));
    navigate("/create-task");
  }, [
    navigate,
    selectedStatuses,
    selectedPriorities,
    selectedApplications,
    selectedReleaseTypes,
    selectedTaskTypes,
    selectedAgingBuckets,
    internalOverdueClientFilter,
    internalUnassignedClientFilter,
    searchTerm,
    ownershipFilter,
    searchMode,
    searchPanelExpanded,
    selectedTaskOwnerIds,
    selectedCreatedByIds,
    releaseDateFilter,
  ]);

  const handleUpdateTask = useCallback((taskId) => {
    // Save current filters before navigating
    const filterState = {
      selectedStatuses,
      selectedPriorities,
      selectedApplications,
      selectedReleaseTypes,
      selectedTaskTypes,
      selectedAgingBuckets,
      internalOverdueClientFilter,
      internalUnassignedClientFilter,
      searchTerm,
      ownershipFilter,
      searchMode,
      searchPanelExpanded,
      selectedTaskOwnerIds,
      selectedCreatedByIds,
      releaseDateFilter,
    };
    localStorage.setItem("taskFilters", JSON.stringify(filterState));
    navigate(`/update-task/${taskId}`);
  }, [
    navigate,
    selectedStatuses,
    selectedPriorities,
    selectedApplications,
    selectedReleaseTypes,
    selectedTaskTypes,
    selectedAgingBuckets,
    internalOverdueClientFilter,
    internalUnassignedClientFilter,
    searchTerm,
    ownershipFilter,
    searchMode,
    searchPanelExpanded,
    selectedTaskOwnerIds,
    selectedCreatedByIds,
    releaseDateFilter,
  ]);

  const handleViewAuditHistory = useCallback((taskId) => {
    setSelectedTaskId(taskId);
    setShowAuditDialog(true);
  }, []);

  const closeAuditDialog = useCallback(() => {
    setShowAuditDialog(false);
    setSelectedTaskId(null);
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedStatuses(["In Progress", "Not started"]); // Reset to default "In Progress" and "Not started"
    setSelectedPriorities([]); // Reset to default (no priority filter)
    setSelectedReleaseTypes([]); // Reset release type filter
    setSelectedApplications([]); // Reset to empty - no default application filter
    setSelectedTaskTypes([]);
    setSelectedAgingBuckets([]);
    setInternalOverdueClientFilter(false);
    setInternalUnassignedClientFilter(false);
    setSearchTerm("");
    setOwnershipFilter(getDefaultOwnershipFilter()); // Reset to "Assigned"
    setSearchMode("basic");
    setSearchPanelExpanded(true);
    setSelectedTaskOwnerIds([]);
    setSelectedCreatedByIds([]);
    setReleaseDateFilter("");
    setNoDataFound(false); // Reset no data found state
    setCurrentPage(1); // Reset to first page
  }, [getDefaultOwnershipFilter]);
  
  // Handle page change
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // Debug logging for current state
  console.log("🔧 Current Filter State:", {
    selectedStatuses,
    selectedPriorities,
    selectedApplications,
    selectedReleaseTypes,
    ownershipFilter,
    searchMode,
    selectedTaskOwnerIds,
    selectedCreatedByIds,
    releaseDateFilter,
    searchTerm: `"${searchTerm}"`,
    searchTermLength: searchTerm.length,
    filteredTasksCount: processedTasks.length,
    totalTasksCount: allTasks.length,
    leadNames,
    isComingFromDashboard
  });

  // Render error state
  if (error) {
    return (
      <div className="all-tasks-screen">
        <div className="screen-content-wrapper">
          <div className="all-tasks-search-panel all-tasks-search-panel--modern all-tasks-top-bar">
            <div className="all-tasks-search-panel-header">
              <h1 className="screen-title all-tasks-bar-title">All Tasks</h1>
              <div className="all-tasks-bar-actions">
                {canCreateOrEditTask() && (
                  <button type="button" className="btn btn-primary" onClick={handleCreateTask}>
                    Create Task
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={handleBack}>
                  Back
                </button>
              </div>
            </div>
          </div>
          <div className="error-message">{error}</div>
          <button
            className="btn btn-primary retry-button"
            type="button"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const horizontalFilterStyles = {
    filters: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      columnGap: '28px',
      rowGap: '12px',
      flexWrap: 'wrap',
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      margin: '0 2px',
    },
    /** Width comes from CSS (--all-tasks-filter-field-width on .all-tasks-filter-toolbar). */
    typeaheadField: {
      fontFamily: 'inherit',
    },
  };

  return (
    <div className="all-tasks-screen">
      <div className="screen-content-wrapper">
        <div
          className="all-tasks-search-panel all-tasks-search-panel--modern"
          ref={agingChartWrapRef}
        >
          <div className="all-tasks-search-panel-header">
            <div className="all-tasks-search-panel-header-left">
              <h1 className="screen-title all-tasks-bar-title">All Tasks</h1>
              <div className="all-tasks-search-panel-divider" aria-hidden="true" />
              <span className="all-tasks-search-panel-title">Search</span>
              <div
                className="all-tasks-search-mode-toggle all-tasks-search-mode-toggle--segmented"
                role="radiogroup"
                aria-label="Search mode"
              >
                <label className={searchMode === "basic" ? "is-active" : ""}>
                  <input
                    type="radio"
                    name="allTasksSearchMode"
                    checked={searchMode === "basic"}
                    onChange={() => setSearchMode("basic")}
                  />
                  <span className="all-tasks-search-mode-label-text">Basic</span>
                </label>
                <label className={searchMode === "advanced" ? "is-active" : ""}>
                  <input
                    type="radio"
                    name="allTasksSearchMode"
                    checked={searchMode === "advanced"}
                    onChange={() => setSearchMode("advanced")}
                  />
                  <span className="all-tasks-search-mode-label-text">Advanced</span>
                </label>
              </div>
              <div className="all-tasks-aging-chart-anchor">
                <button
                  type="button"
                  className={`btn btn-secondary all-tasks-aging-chart-header-btn${
                    agingChartOpen ? " is-open" : ""
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSearchPanelExpanded(true);
                    handleToggleAgingChart();
                  }}
                  aria-expanded={agingChartOpen}
                  aria-haspopup="dialog"
                  aria-controls="all-tasks-aging-chart-popover"
                  title="Ticket ageing breakdown"
                  aria-label="Show ticket ageing pie chart"
                >
                  Ageing
                  <span className="all-tasks-aging-chart-header-btn__caret" aria-hidden="true">
                    {agingChartOpen ? "▲" : "▼"}
                  </span>
                </button>
                {agingChartOpen ? (
                  <div
                    id="all-tasks-aging-chart-popover"
                    className="all-tasks-aging-chart-popover all-tasks-aging-chart-popover--header"
                    role="dialog"
                    aria-label="Ticket ageing counts"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div className="all-tasks-aging-chart-popover__head">
                      <div>
                        <strong>Ticket ageing</strong>
                        <span>Counts by age bucket (current filters)</span>
                      </div>
                      <button
                        type="button"
                        className="all-tasks-aging-chart-popover__close"
                        onClick={() => setAgingChartOpen(false)}
                        aria-label="Close ageing chart"
                      >
                        ×
                      </button>
                    </div>

                    {agingChartLoading ? (
                      <div className="all-tasks-aging-chart-popover__loading">
                        Loading ageing counts...
                      </div>
                    ) : agingChartError ? (
                      <div className="all-tasks-aging-chart-popover__error">
                        {agingChartError}
                      </div>
                    ) : (
                      <div className="all-tasks-aging-chart-popover__body">
                        <div
                          className="all-tasks-aging-chart-donut"
                          style={{ background: agingChartDonutGradient }}
                          aria-label={`${agingChartTotalTickets} tickets`}
                        >
                          <div className="all-tasks-aging-chart-donut__hole">
                            <strong>{agingChartTotalTickets}</strong>
                            <span>tickets</span>
                          </div>
                        </div>
                        <ul className="all-tasks-aging-chart-legend">
                          {AGING_BUCKET_FILTER_OPTIONS.map((bucket) => {
                            const count = Number(agingChartTotals[bucket.value] || 0);
                            const pct = agingChartTotalTickets
                              ? Math.round((count / agingChartTotalTickets) * 100)
                              : 0;
                            return (
                              <li key={bucket.value}>
                                <button
                                  type="button"
                                  className="all-tasks-aging-chart-legend__item"
                                  onClick={() => handleAgingChartBucketClick(bucket.value)}
                                  title={`Filter by ${bucket.label}`}
                                >
                                  <span
                                    className="all-tasks-aging-chart-legend__swatch"
                                    style={{ background: bucket.color }}
                                    aria-hidden="true"
                                  />
                                  <span className="all-tasks-aging-chart-legend__label">
                                    {bucket.label}
                                  </span>
                                  <span className="all-tasks-aging-chart-legend__count">
                                    {count}
                                  </span>
                                  <span className="all-tasks-aging-chart-legend__pct">
                                    {pct}%
                                  </span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                    <p className="all-tasks-aging-chart-popover__hint">
                      Click a bucket to filter tasks by that age range.
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="all-tasks-bar-actions">
              <button
                type="button"
                className="all-tasks-search-toggle-btn"
                onClick={() => setSearchPanelExpanded((prev) => !prev)}
                aria-expanded={searchPanelExpanded}
                aria-label={searchPanelExpanded ? "Collapse search filters" : "Expand search filters"}
                title={searchPanelExpanded ? "Collapse" : "Expand"}
              >
                <span className="all-tasks-search-toggle-icon" aria-hidden="true">
                  {searchPanelExpanded ? "▲" : "▼"}
                </span>
              </button>
              {canCreateOrEditTask() && (
                <button type="button" className="btn btn-primary" onClick={handleCreateTask}>
                  Create Task
                </button>
              )}
              <button type="button" className="btn btn-secondary" onClick={handleBack}>
                Back
              </button>
            </div>
          </div>
          {searchPanelExpanded && (
            <div className="all-tasks-search-panel-body horizontal-filters-container">
              <div
                className={`horizontal-filters all-tasks-filter-toolbar ${
                  searchMode === "basic"
                    ? "all-tasks-filter-toolbar--basic"
                    : "all-tasks-filter-toolbar--advanced"
                }`}
                style={horizontalFilterStyles.filters}
              >
                <div
                  className="all-tasks-filter-group all-tasks-main-search-wrap"
                  style={horizontalFilterStyles.filterGroup}
                >
                  <label className="all-tasks-filter-label" htmlFor="all-tasks-main-search-input">
                    Search
                  </label>
                  <div className="all-tasks-filter-control-shell">
                    <input
                      id="all-tasks-main-search-input"
                      className="all-tasks-main-search-input"
                      type="text"
                      placeholder="Search tasks, assignees, owners..."
                      value={searchTerm}
                      title={searchTerm.trim() ? searchTerm : undefined}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setSearchTerm(newValue);
                        setNoDataFound(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setCurrentPage(1);
                          fetchTasksWithFilters(true);
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="all-tasks-filter-group" style={horizontalFilterStyles.filterGroup}>
                  <label className="all-tasks-filter-label">Status</label>
                  <TypeAheadDropdown
                    label="Statuses"
                    options={statuses.sort((a, b) => a.aa_app_rl_task_status_id - b.aa_app_rl_task_status_id)}
                    selectedValues={selectedStatuses}
                    onToggle={handleStatusToggle}
                    displayKey="aa_app_rl_task_status_name"
                    valueKey="aa_app_rl_task_status_name"
                    placeholder="Search statuses..."
                    showAllOption={true}
                    isMultiSelect={true}
                    customStyle={horizontalFilterStyles.typeaheadField}
                    clearable={true}
                    minCharsToSearch={0}
                    sleek
                  />
                </div>

                <div className="all-tasks-filter-group" style={horizontalFilterStyles.filterGroup}>
                  <label className="all-tasks-filter-label">Application</label>
                  <TypeAheadDropdown
                    label="Applications"
                    options={
                      applications && applications.length > 0
                        ? applications
                            .slice()
                            .sort((a, b) => {
                              const nameA = a?.aa_app_rl_application_name || "";
                              const nameB = b?.aa_app_rl_application_name || "";
                              return nameA.localeCompare(nameB);
                            })
                        : []
                    }
                    selectedValues={selectedApplications}
                    onToggle={handleApplicationToggle}
                    displayKey="aa_app_rl_application_name"
                    valueKey="aa_app_rl_application_id"
                    placeholder={
                      applications && applications.length > 0
                        ? "Search applications..."
                        : "Loading applications..."
                    }
                    showAllOption={true}
                    isMultiSelect={true}
                    customStyle={horizontalFilterStyles.typeaheadField}
                    clearable={true}
                    minCharsToSearch={1}
                    sleek
                  />
                </div>

                <div className="all-tasks-filter-group" style={horizontalFilterStyles.filterGroup}>
                  <label className="all-tasks-filter-label">Task Relation</label>
                  <TypeAheadDropdown
                    label="Task Relations"
                    options={getTaskRelationOptions()}
                    selectedValues={[ownershipFilter]}
                    onToggle={(value, mode) => {
                      if (mode === "single") {
                        setOwnershipFilter(value);
                      }
                    }}
                    displayKey="name"
                    valueKey="id"
                    placeholder="Select task relation..."
                    showAllOption={false}
                    isMultiSelect={false}
                    customStyle={horizontalFilterStyles.typeaheadField}
                    clearable={false}
                    minCharsToSearch={0}
                    sleek
                  />
                </div>

                {searchMode === "advanced" && (
                  <>
                    <div className="all-tasks-filter-group" style={horizontalFilterStyles.filterGroup}>
                      <label className="all-tasks-filter-label">Priority</label>
                      <TypeAheadDropdown
                        label="Priorities"
                        options={priorities}
                        selectedValues={selectedPriorities}
                        onToggle={handlePriorityToggle}
                        displayKey="aa_app_rl_priority_name"
                        valueKey="aa_app_rl_priority_name"
                        placeholder={
                          priorities.length > 0
                            ? "Search priorities..."
                            : "Loading priorities (open Dashboard once)..."
                        }
                        showAllOption={true}
                        isMultiSelect={true}
                        customStyle={horizontalFilterStyles.typeaheadField}
                        clearable={true}
                        minCharsToSearch={0}
                        sleek
                        disabled={priorities.length === 0}
                      />
                    </div>

                    <div className="all-tasks-filter-group" style={horizontalFilterStyles.filterGroup}>
                      <label className="all-tasks-filter-label">Task Type</label>
                      <TypeAheadDropdown
                        label="Task Types"
                        options={taskTypeOptions}
                        selectedValues={selectedTaskTypes}
                        onToggle={handleTaskTypeToggle}
                        displayKey="label"
                        valueKey="value"
                        placeholder={
                          taskTypeOptions.length > 0
                            ? "Search task types..."
                            : "Loading task types..."
                        }
                        showAllOption={true}
                        isMultiSelect={true}
                        customStyle={horizontalFilterStyles.typeaheadField}
                        clearable={true}
                        minCharsToSearch={0}
                        sleek
                        disabled={taskTypeOptions.length === 0}
                      />
                    </div>

                    <div className="all-tasks-filter-group" style={horizontalFilterStyles.filterGroup}>
                      <label className="all-tasks-filter-label">Age bucket</label>
                      <TypeAheadDropdown
                        label="Age buckets"
                        options={AGING_BUCKET_FILTER_OPTIONS}
                        selectedValues={selectedAgingBuckets}
                        onToggle={handleAgingBucketToggle}
                        displayKey="label"
                        valueKey="value"
                        placeholder="All age buckets"
                        showAllOption={true}
                        isMultiSelect={true}
                        customStyle={horizontalFilterStyles.typeaheadField}
                        clearable={true}
                        minCharsToSearch={0}
                        sleek
                      />
                    </div>

                    <div className="all-tasks-filter-group" style={horizontalFilterStyles.filterGroup}>
                      <label className="all-tasks-filter-label">Created by</label>
                      <TypeAheadDropdown
                        label="Created by"
                        options={createdByDropdownOptions}
                        selectedValues={selectedCreatedByIds}
                        onToggle={handleCreatedByToggle}
                        displayKey="name"
                        valueKey="id"
                        placeholder={
                          !masterEmployeeList.length
                            ? "Loading employees (open Dashboard once)..."
                            : "Search creators..."
                        }
                        showAllOption={false}
                        isMultiSelect={true}
                        customStyle={horizontalFilterStyles.typeaheadField}
                        clearable={true}
                        minCharsToSearch={0}
                        sleek
                        disabled={!masterEmployeeList.length}
                      />
                    </div>

                    <div
                      className="all-tasks-filter-group"
                      style={horizontalFilterStyles.filterGroup}
                      title="Owners are limited to people allowed for the Application(s) you selected above (same rules as Create Task). Use Search to apply."
                    >
                      <label className="all-tasks-filter-label">Task Owner</label>
                      <TypeAheadDropdown
                        label="Task Owner"
                        options={taskOwnerDropdownOptions}
                        selectedValues={selectedTaskOwnerIds}
                        onToggle={handleTaskOwnerToggle}
                        displayKey="name"
                        valueKey="id"
                        placeholder={
                          !ownerFilterEmployees.length
                            ? "Loading employees (open Dashboard once)..."
                            : selectedApplications.length === 0
                              ? "Select application(s) first..."
                              : "Search task owners..."
                        }
                        showAllOption={false}
                        isMultiSelect={true}
                        customStyle={horizontalFilterStyles.typeaheadField}
                        clearable={true}
                        minCharsToSearch={0}
                        sleek
                        disabled={
                          !ownerFilterEmployees.length || selectedApplications.length === 0
                        }
                      />
                    </div>

                    <div className="all-tasks-filter-group" style={horizontalFilterStyles.filterGroup}>
                      <label className="all-tasks-filter-label">Release Type</label>
                      <TypeAheadDropdown
                        label="Release Types"
                        options={releaseTypeOptions}
                        selectedValues={selectedReleaseTypes}
                        onToggle={handleReleaseTypeToggle}
                        displayKey="label"
                        valueKey="value"
                        placeholder={
                          releaseTypeOptions.length > 0
                            ? "Search release types..."
                            : "Loading release types..."
                        }
                        showAllOption={true}
                        isMultiSelect={true}
                        customStyle={horizontalFilterStyles.typeaheadField}
                        clearable={true}
                        minCharsToSearch={0}
                        sleek
                      />
                    </div>

                    <div className="all-tasks-filter-group" style={horizontalFilterStyles.filterGroup}>
                      <label
                        className="all-tasks-filter-label"
                        htmlFor="all-tasks-overdue-only-checkbox"
                      >
                        Overdue
                      </label>
                      <div className="all-tasks-filter-control-shell all-tasks-filter-control-shell--compact all-tasks-overdue-toggle-shell">
                        <label
                          htmlFor="all-tasks-overdue-only-checkbox"
                          className={`all-tasks-overdue-toggle-label ${
                            internalOverdueClientFilter ? "is-active" : ""
                          }`}
                        >
                          <input
                            id="all-tasks-overdue-only-checkbox"
                            className="all-tasks-overdue-toggle-checkbox"
                            type="checkbox"
                            checked={!!internalOverdueClientFilter}
                            onChange={handleOverdueOnlyToggle}
                          />
                          <span className="all-tasks-overdue-toggle-text">
                            Overdue only
                          </span>
                          <span className="all-tasks-overdue-toggle-track" aria-hidden="true">
                            <span className="all-tasks-overdue-toggle-thumb" />
                          </span>
                        </label>
                      </div>
                    </div>

                    <div className="all-tasks-filter-group" style={horizontalFilterStyles.filterGroup}>
                      <label
                        className="all-tasks-filter-label"
                        htmlFor="all-tasks-unassigned-only-checkbox"
                      >
                        Unassigned
                      </label>
                      <div className="all-tasks-filter-control-shell all-tasks-filter-control-shell--compact all-tasks-overdue-toggle-shell all-tasks-unassigned-toggle-shell">
                        <label
                          htmlFor="all-tasks-unassigned-only-checkbox"
                          className={`all-tasks-overdue-toggle-label ${
                            internalUnassignedClientFilter ? "is-active" : ""
                          }`}
                        >
                          <input
                            id="all-tasks-unassigned-only-checkbox"
                            className="all-tasks-overdue-toggle-checkbox"
                            type="checkbox"
                            checked={!!internalUnassignedClientFilter}
                            onChange={handleUnassignedOnlyToggle}
                          />
                          <span className="all-tasks-overdue-toggle-text">Unassigned only</span>
                          <span className="all-tasks-overdue-toggle-track" aria-hidden="true">
                            <span className="all-tasks-overdue-toggle-thumb" />
                          </span>
                        </label>
                      </div>
                    </div>

                    <div className="all-tasks-filter-group" style={horizontalFilterStyles.filterGroup}>
                      <label className="all-tasks-filter-label" htmlFor="all-tasks-release-date-input">
                        Release Date
                      </label>
                      <div className="all-tasks-filter-control-shell all-tasks-filter-control-shell--compact">
                        <input
                          id="all-tasks-release-date-input"
                          className="all-tasks-filter-date-input"
                          type="date"
                          value={releaseDateFilter}
                          onChange={(e) => setReleaseDateFilter(e.target.value)}
                          title="Choose a release date, then click Search to apply (does not filter the table while typing)"
                        />
                      </div>
                    </div>
                  </>
                )}

                {searchMode === "advanced" ? (
                  <div className="all-tasks-search-actions-row">
                    <button
                      className="btn btn-primary"
                      type="button"
                      disabled={loading}
                      onClick={() => {
                        setCurrentPage(1);
                        fetchTasksWithFilters(true);
                      }}
                      title="Search tasks with current filters"
                    >
                      Search
                    </button>

                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={clearFilters}
                      title="Reset to default filters"
                    >
                      Reset
                    </button>
                  </div>
                ) : (
                  <div className="all-tasks-filter-group" style={horizontalFilterStyles.filterGroup}>
                    <span
                      className="all-tasks-filter-label all-tasks-filter-label--invisible"
                      aria-hidden="true"
                    >
                      Search
                    </span>
                    <div className="all-tasks-filter-toolbar-actions">
                      <button
                        className="btn btn-primary"
                        type="button"
                        disabled={loading}
                        onClick={() => {
                          setCurrentPage(1);
                          fetchTasksWithFilters(true);
                        }}
                        title="Search tasks with current filters"
                      >
                        Search
                      </button>

                      <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={clearFilters}
                        title="Reset to default filters"
                      >
                        Reset
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Show "No data found" message when 404 error occurs during search */}
        {noDataFound && !loading && (
          <div style={{
            textAlign: 'center',
            padding: '20px',
            margin: '20px 0',
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            color: '#6c757d'
          }}>
            <p style={{ margin: 0, fontSize: '12px', fontWeight: '500' }}>No data found</p>
          </div>
        )}

        <div className="tasks-table-container all-tasks-table-area">
          {loading ? (
            <div className="all-tasks-loading-state" aria-busy="true" aria-live="polite">
              <div className="all-tasks-loading-content">
                <div className="loading-message all-tasks-loading-message">Loading tasks…</div>
                <div
                  className="all-tasks-loading-spinner"
                  aria-hidden="true"
                />
              </div>
            </div>
          ) : processedTasks.length === 0 && !noDataFound ? (
            <div className="no-tasks-message">
              <p>No tasks found matching your filters.</p>
              <button className="btn btn-primary" onClick={clearFilters}>
                Reset Filters
              </button>
            </div>
          ) : processedTasks.length > 0 ? (
            <div
              className="tasks-table-scroll"
              role="region"
              aria-label="Task list table. Scroll horizontally to view all columns when zoomed or on a small screen."
            >
            <table className="tasks-table" style={{ 
              fontSize: '10px', 
              tableLayout: 'fixed', 
              borderCollapse: 'collapse'
            }}>
              <thead>
                <tr>
                  <th 
                    className="sortable-header"
                    style={{ width: '60px', padding: '4px 6px', cursor: 'pointer' }}
                    onClick={() => handleColumnSort('id')}
                    title="Click to sort by ID (Ascending → Descending → No Sort)"
                  >
                    ID{getSortIndicator('id')}{getSortOrder('id')}
                  </th>
                  <th 
                    className="sortable-header"
                    style={{ width: '150px', padding: '4px 6px', cursor: 'pointer' }}
                    onClick={() => handleColumnSort('title')}
                    title="Click to sort by Title (Ascending → Descending → No Sort)"
                  >
                    Title{getSortIndicator('title')}{getSortOrder('title')}
                  </th>
                  <th 
                    className="sortable-header"
                    style={{ width: '70px', padding: '4px 6px', cursor: 'pointer' }}
                    onClick={() => handleColumnSort('priority')}
                    title="Click to sort by Priority (Ascending → Descending → No Sort)"
                  >
                    Priority{getSortIndicator('priority')}{getSortOrder('priority')}
                  </th>
                  <th 
                    className="sortable-header"
                    style={{ width: '70px', padding: '4px 6px', cursor: 'pointer' }}
                    onClick={() => handleColumnSort('type')}
                    title="Click to sort by Type (Ascending → Descending → No Sort)"
                  >
                    Type{getSortIndicator('type')}{getSortOrder('type')}
                  </th>
                  <th 
                    className="sortable-header"
                    style={{ width: '120px', padding: '4px 6px', cursor: 'pointer' }}
                    onClick={() => handleColumnSort('application')}
                    title="Click to sort by Application (Ascending → Descending → No Sort)"
                  >
                    Application{getSortIndicator('application')}{getSortOrder('application')}
                  </th>
                  <th 
                    className="sortable-header"
                    style={{ width: '110px', padding: '4px 6px', cursor: 'pointer' }}
                    onClick={() => handleColumnSort('assignedTo')}
                    title="Click to sort by Assigned To (Ascending → Descending → No Sort)"
                  >
                    Assigned To{getSortIndicator('assignedTo')}{getSortOrder('assignedTo')}
                  </th>
                  <th 
                    className="sortable-header"
                    style={{ width: '100px', padding: '4px 6px', cursor: 'pointer' }}
                    onClick={() => handleColumnSort('taskOwner')}
                    title="Click to sort by Task Owner (Ascending → Descending → No Sort)"
                  >
                    Task Owner{getSortIndicator('taskOwner')}{getSortOrder('taskOwner')}
                  </th>
                  <th 
                    className="sortable-header"
                    style={{ width: '80px', padding: '4px 6px', cursor: 'pointer' }}
                    onClick={() => handleColumnSort('status')}
                    title="Click to sort by Status (Ascending → Descending → No Sort)"
                  >
                    Status{getSortIndicator('status')}{getSortOrder('status')}
                  </th>
                  <th 
                    className="sortable-header"
                    style={{ width: '85px', padding: '4px 6px', cursor: 'pointer' }}
                    onClick={() => handleColumnSort('createdOn')}
                    title="Click to sort by Created On (Ascending → Descending → No Sort)"
                  >
                    Created On{getSortIndicator('createdOn')}{getSortOrder('createdOn')}
                  </th>
                  <th 
                    className="sortable-header"
                    style={{ width: '85px', padding: '4px 6px', cursor: 'pointer' }}
                    onClick={() => handleColumnSort('lastUpdatedOn')}
                    title="Click to sort by Last Updated On (Ascending → Descending → No Sort)"
                  >
                    Last Updated On{getSortIndicator('lastUpdatedOn')}{getSortOrder('lastUpdatedOn')}
                  </th>
                  <th
                    className="sortable-header"
                    style={{ width: '95px', padding: '4px 6px', cursor: 'pointer' }}
                    onClick={() => handleColumnSort('releaseType')}
                    title="Click to sort by Release Type (Ascending → Descending → No Sort)"
                  >
                    Release Type{getSortIndicator('releaseType')}{getSortOrder('releaseType')}
                  </th>
                  <th
                    className="sortable-header"
                    style={{ width: '85px', padding: '4px 6px', cursor: 'pointer' }}
                    onClick={() => handleColumnSort('releaseDate')}
                    title="Click to sort by Release Date (Ascending → Descending → No Sort)"
                  >
                    Release Date{getSortIndicator('releaseDate')}{getSortOrder('releaseDate')}
                  </th>
                  <th style={{ width: '70px', padding: '4px 6px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {processedTasks.map((task) => (
                  <tr key={task.id}>
                    <td style={{ 
                      padding: '4px 6px', 
                      textAlign: 'center',
                      fontSize: '9px'
                    }}>
                      {task.id}
                    </td>
                    <td style={{ 
                      padding: '4px 6px', 
                      wordWrap: 'break-word',
                      whiteSpace: 'normal',
                      lineHeight: '1.2',
                      maxHeight: '50px',
                      overflow: 'hidden',
                      textAlign: 'left'
                    }}>
                      <div title={task.title} style={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {task.title}
                      </div>
                    </td>
                    <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                      <span
                        className={`priority-badge ${task.priority
                          .toLowerCase()
                          .replace(/\s+/g, "-")}`}
                        style={{
                          fontSize: '8px',
                          padding: '2px 4px',
                          borderRadius: '3px'
                        }}
                      >
                        {task.priority}
                      </span>
                    </td>
                    <td style={{ 
                      padding: '4px 6px', 
                      textAlign: 'center',
                      fontSize: '9px'
                    }}>
                      <span
                        className={`task-type-badge ${task.taskType
                          .toLowerCase()
                          .replace(/\s+/g, "-")}`}
                        title={task.taskTypeLongName || task.taskType} // Show long name in tooltip
                      >
                        {task.taskType}
                      </span>
                    </td>
                    <td style={{ 
                      padding: '4px 6px',
                      wordWrap: 'break-word',
                      whiteSpace: 'normal',
                      lineHeight: '1.2'
                    }}>
                      <div title={task.application} style={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {task.application}
                      </div>
                    </td>
                    <td style={{ 
                      padding: '4px 6px',
                      wordWrap: 'break-word',
                      whiteSpace: 'normal',
                      lineHeight: '1.2'
                    }}>
                      <div title={task.employeeName} style={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {task.employeeName}
                      </div>
                    </td>
                    <td style={{ 
                      padding: '4px 6px',
                      wordWrap: 'break-word',
                      whiteSpace: 'normal',
                      lineHeight: '1.2',
                      textAlign: 'left'
                    }}>
                      <div title={task.ownerName} style={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {task.ownerName}
                      </div>
                    </td>
                    <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                      <span
                        className={`status-badge ${task.status
                          .toLowerCase()
                          .replace(/\s+/g, "-")}`}
                        style={{
                          fontSize: '9px',
                          padding: '2px 4px',
                          borderRadius: '3px'
                        }}
                      >
                        {task.status}
                      </span>
                    </td>
                    <td style={{ 
                      padding: '4px 6px', 
                      fontSize: '9px',
                      textAlign: 'center'
                    }}>
                      {task.creationDate}
                    </td>
                    <td style={{ 
                      padding: '4px 6px', 
                      fontSize: '9px',
                      textAlign: 'center'
                    }}>
                      {task.updationDate}
                    </td>
                    <td
                      style={{
                        padding: '4px 6px',
                        fontSize: '9px',
                        textAlign: 'center',
                        wordWrap: 'break-word',
                        whiteSpace: 'normal',
                        lineHeight: '1.2',
                      }}
                    >
                      <div
                        title={task.releaseTypeName || ''}
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {task.releaseTypeName || 'N/A'}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: '4px 6px',
                        fontSize: '9px',
                        textAlign: 'center',
                      }}
                    >
                      {task.releaseDate}
                    </td>
                    <td style={{ padding: '4px 6px', textAlign: 'center' }}>
                      <div className="action-icons" style={{ 
                        display: 'flex', 
                        gap: '4px', 
                        justifyContent: 'center' 
                      }}>
                        {/* Conditionally render Update Task button (pencil icon) - UPDATED FOR ROLE_ID = 3 */}
                        {canCreateOrEditTask() && (
                          <button
                            className="btn-icon"
                            onClick={() => handleUpdateTask(task.id)}
                            title="Update Status"
                          >
                            <i className="fas fa-pencil-alt"></i>
                          </button>
                        )}
                        <button
                          className="btn-icon"
                          onClick={() => handleViewAuditHistory(task.id)}
                          title="View History"
                        >
                          <i className="fas fa-history"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          ) : null}
          
          {/* Pagination Component */}
          {!loading && processedTasks.length > 0 && totalCount > itemsPerPage && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: '20px',
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              <button
                type="button"
                className="btn btn-secondary btn-compact"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1
                }}
              >
                Previous
              </button>
              
              {/* Page Numbers */}
              {(() => {
                const totalPages = Math.ceil(totalCount / itemsPerPage);
                const pages = [];
                const maxVisiblePages = 5;
                
                let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                
                if (endPage - startPage < maxVisiblePages - 1) {
                  startPage = Math.max(1, endPage - maxVisiblePages + 1);
                }
                
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <button
                      type="button"
                      key={i}
                      className={
                        i === currentPage
                          ? "btn btn-primary btn-compact"
                          : "btn btn-secondary btn-compact"
                      }
                      onClick={() => handlePageChange(i)}
                      style={{
                        minWidth: '36px',
                        fontWeight: i === currentPage ? 'bold' : 'normal'
                      }}
                    >
                      {i}
                    </button>
                  );
                }
                
                return pages;
              })()}
              
              <button
                type="button"
                className="btn btn-secondary btn-compact"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= Math.ceil(totalCount / itemsPerPage)}
                style={{
                  cursor: currentPage >= Math.ceil(totalCount / itemsPerPage) ? 'not-allowed' : 'pointer',
                  opacity: currentPage >= Math.ceil(totalCount / itemsPerPage) ? 0.5 : 1
                }}
              >
                Next
              </button>
              
              <span style={{
                marginLeft: '12px',
                fontSize: '11px',
                color: '#666'
              }}>
                Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} (Page {currentPage})
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Render the Audit History Dialog if shown */}
      {showAuditDialog && selectedTaskId && (
        <AuditHistoryDialog
          taskId={selectedTaskId}
          onClose={closeAuditDialog}
        />
      )}
    </div>
  );
};

export default AllTasksScreen;